<!-- ============================================================
  GroupingSheet.vue — จัดกลุ่มโค้ชในคลาส (แทน Google Sheet เดิม)

  Sheet-like grid: day mode = one column per class session, week
  mode = one column per day with sessions stacked. Each session
  column stacks coach-subgroup blocks + the "ยังไม่จัดกลุ่ม" block.

  Two hosts (App.vue):
    • state 'groupingmanager' → manager mode (editable) — assign
      students via the ▾ row menu, add/edit/delete groups, session
      + group notes, coach absences (all-day or per-class), and the
      coach roster dialog (coaches don't need a login; link later).
    • state 'coachschedule'  → <grouping-sheet coach-mode> —
      read-only view for coaches; groups where the logged-in user's
      linked coach appears are highlighted (isMine from backend).
      Exception: the Private-lesson card IS interactive — head/admin
      or the assigned coach may check in/undo check-in a session.

  Data: /getClassGrouping (manager day) · /getWeekGrouping (manager
  week) · /getMyCoachSchedule (coach mode). Mutations refetch
  silently (no skeleton) to keep the grid steady.
============================================================ -->
<template>
  <div>
    <div class="pg-head">
      <div class="pg-ico"><span class="mdi" :class="coachMode ? 'mdi-clipboard-account-outline' : 'mdi-account-group-outline'"></span></div>
      <div>
        <div class="id-h1">{{ coachMode ? $t('grouping.coachTitle') : $t('grouping.title') }}</div>
        <div class="pg-sub">{{ coachMode ? $t('grouping.coachSubtitle') : $t('grouping.subtitle') }}</div>
      </div>
      <span v-if="viewers.length" class="pres-viewers"
        v-hint="$t('presence.viewersHint', { names: viewers.join(', ') })"
        :aria-label="$t('presence.viewersHint', { names: viewers.join(', ') })">
        <span class="mdi mdi-account-eye"></span>{{ viewers.length }}
      </span>
    </div>

    <!-- toolbar: date nav + mode toggle + actions -->
    <div class="gs-toolbar">
      <div class="gs-nav">
        <button type="button" class="id-btn id-btn-ghost gs-navbtn" :aria-label="$t('grouping.prev')" @click="goPrev"><span class="mdi mdi-chevron-left"></span></button>
        <span v-if="mode === 'day'" class="gs-navlabel">{{ $formatFullDate(selectedDate) }}</span>
        <span v-else class="gs-navlabel">{{ weekLabel }}</span>
        <button type="button" class="id-btn id-btn-ghost gs-navbtn" :aria-label="$t('grouping.next')" @click="goNext"><span class="mdi mdi-chevron-right"></span></button>
        <div v-if="mode === 'day'" class="gs-datejump"><id-date v-model="selectedDate" :placeholder="$t('common.selectDate')" @update:model-value="onDatePick"></id-date></div>
        <button type="button" class="id-btn id-btn-ghost gs-navbtn" @click="goToday">{{ $t('grouping.today') }}</button>
      </div>
      <div class="gs-actions">
        <div class="gs-modes">
          <button type="button" class="id-btn" :class="mode === 'day' ? 'id-btn-primary' : 'id-btn-ghost'" @click="setMode('day')">{{ $t('grouping.modeDay') }}</button>
          <button type="button" class="id-btn" :class="mode === 'week' ? 'id-btn-primary' : 'id-btn-ghost'" @click="setMode('week')">{{ $t('grouping.modeWeek') }}</button>
        </div>
        <template v-if="!coachMode">
          <!-- ทั้งวัน = วนเรียกพรีวิวทีละคาบแล้วรวมผลในกล่องเดียว (ไม่มี endpoint ระดับวัน โดยตั้งใจ —
               คาบไหนพังก็ยังจัดคาบที่เหลือได้). โหมดสัปดาห์ไม่มีปุ่มนี้: พรีวิว 7 วันในกล่องเดียวอ่านไม่ไหว -->
          <button v-if="mode === 'day' && dayHasRuleSessions" type="button" class="id-btn id-btn-ghost gs-autobtn" @click="openAutoDialog(null)">
            <span class="mdi mdi-auto-fix"></span> {{ $t('grouping.autoBtnDay') }}</button>
          <button type="button" class="id-btn id-btn-ghost" @click="openRuleDialog"><span class="mdi mdi-tune-variant"></span> {{ $t('grouping.ruleBtn') }}</button>
          <button type="button" class="id-btn id-btn-ghost" @click="openAbsenceDialog(null)"><span class="mdi mdi-account-off"></span> {{ $t('grouping.absenceBtn') }}</button>
          <button type="button" class="id-btn id-btn-ghost" @click="openCoachDialog"><span class="mdi mdi-account-cog-outline"></span> {{ $t('grouping.coachManageBtn') }}</button>
        </template>
        <template v-else>
          <button type="button" class="id-btn" :class="highlightMine ? 'id-btn-primary' : 'id-btn-ghost'" :disabled="myCoachId == null" @click="highlightMine = !highlightMine">
            <span class="mdi mdi-star"></span> {{ $t('grouping.highlightMine') }}</button>
          <button type="button" class="id-btn" :class="onlyMine ? 'id-btn-primary' : 'id-btn-ghost'" :disabled="myCoachId == null" @click="onlyMine = !onlyMine">
            <span class="mdi mdi-filter"></span> {{ $t('grouping.myGroupsOnly') }}</button>
        </template>
      </div>
    </div>

    <!-- day mode: หมายเหตุประจำวัน — แยกจากหมายเหตุประจำคลาสในแต่ละคอลัมน์ (ไม่ผูกกับคลาสใดคลาสหนึ่ง)
         manager เห็นช่องว่างเชิญให้กดเพิ่มเสมอ, โค้ช (readonly) เห็นเฉพาะตอนมีหมายเหตุแล้ว -->
    <div v-if="mode === 'day' && dayData && (dayData.daynote || !coachMode)" class="gs-daynote"
      :class="{ clickable: !coachMode, empty: !dayData.daynote }"
      @click="!coachMode && openDayNoteDialog(dayData.classdate, $formatFullDate(dayData.classdate), dayData.daynote)">
      <span class="mdi mdi-notebook-outline"></span>
      <span class="gs-daynote-text">{{ dayData.daynote || $t('grouping.dayNotePh') }}</span>
      <span v-if="!coachMode" class="mdi mdi-pencil gs-daynote-edit"></span>
    </div>

    <!-- day mode: absences of the day as chips (มีหัวข้อกำกับ ไม่งั้นป้าย "<ชื่อ> • ทั้งวัน" ลอยมาเฉย ๆ ดูงง) -->
    <div v-if="mode === 'day' && dayData && dayAbsenceChips.length" class="gs-abs-row">
      <span class="gs-abs-title"><span class="mdi mdi-account-off"></span> {{ $t('grouping.absentCoachesToday') }}:</span>
      <span v-for="chip in dayAbsenceChips" :key="chip.coachid" class="gs-abs-chip" :class="{ clickable: !coachMode }"
        @click="!coachMode && openAbsenceDialog(chip)">
        {{ chip.displayname }} • {{ chip.label }}
      </span>
    </div>

    <!-- กริดทั้งหมดอยู่ในการ์ด .scard เหมือน Dashboard/หน้า list มาตรฐาน: scrollbar แนวนอนของ .tt-grid-wrap
         จะถูกกรอบ + เว้นขอบด้วยการ์ด (แทนที่จะลอยบนพื้นหน้า) ให้เหมือนกันทั้งแอป -->
    <div class="scard gs-grid-card">
    <!-- loading skeleton -->
    <div v-if="loading" class="tt-row" style="overflow:hidden">
      <div v-for="i in 4" :key="i" style="min-width:270px;flex:0 0 270px">
        <div class="id-skel" style="height:44px;margin-bottom:6px"></div>
        <div v-for="j in 4" :key="j" class="id-skel" style="height:40px;margin-bottom:6px"></div>
      </div>
    </div>

    <!-- day mode grid -->
    <div v-else-if="mode === 'day'" class="tt-grid-wrap id-fade-in" :key="'d' + selectedDate">
      <div v-if="dayData && dayData.sessions.length" class="tt-row">
        <div v-for="s in dayData.sessions" :key="s.classid || ('ev' + s.eventid)" class="gs-col">
          <sheet-session-column
            :session="s" :absences="dayData.absences" :coach-map="coachMap"
            :readonly="coachMode" :my-coach-id="myCoachId" :checkin-mode="coachMode"
            :highlight-mine="coachMode && highlightMine" :only-mine="onlyMine"
            @assign="openAssignMenu($event, dayData.classdate)"
            @edit-group="openEditGroup($event.session, $event.subgroup, dayData.classdate)"
            @add-group="openAddGroup($event, dayData.classdate)"
            @edit-note="openNoteDialog($event, dayData.classdate)"
            @auto-group="openAutoDialog($event, dayData.classdate)"
            @checkin="doCheckin($event)"></sheet-session-column>
        </div>
      </div>
      <div v-else class="id-empty">
        <div class="mdi mdi-calendar-blank-outline"></div>
        <div style="margin-top:8px">{{ $t('grouping.noSessions') }}</div>
      </div>
    </div>

    <!-- week mode grid: one column per day, sessions stacked -->
    <div v-else class="tt-grid-wrap id-fade-in" :key="'w' + weekStart">
      <div class="tt-row">
        <div v-for="day in days" :key="day.classdate" class="gs-daycol" :class="{ today: day.classdate === todayStr }">
          <div class="gs-dayhead">
            <span class="strong">{{ dayHeadLabel(day.classdate) }}</span>
            <span v-for="chip in absenceChipsFor(day)" :key="chip.coachid" class="gs-abs-chip sm" :class="{ clickable: !coachMode }"
              @click="!coachMode && openAbsenceDialog(chip, day.classdate)">
              <span class="mdi mdi-account-off"></span> {{ chip.displayname }}
            </span>
          </div>
          <div v-if="day.daynote || !coachMode" class="gs-daynote sm"
            :class="{ clickable: !coachMode, empty: !day.daynote }"
            @click="!coachMode && openDayNoteDialog(day.classdate, dayHeadLabel(day.classdate), day.daynote)">
            <span class="mdi mdi-notebook-outline"></span>
            <span class="gs-daynote-text">{{ day.daynote || $t('grouping.dayNotePh') }}</span>
          </div>
          <div v-if="day.sessions.length === 0" class="gs-dayempty">{{ $t('grouping.noSessions') }}</div>
          <div v-for="s in day.sessions" :key="s.classid || ('ev' + s.eventid)" class="gs-daysession">
            <!-- อีเวนต์จัดกลุ่มได้เหมือนคลาสกลุ่มแล้ว (เจ้าของสั่ง 2026-08-13) — readonly เหลือ
                 กรณีเดียวคือโหมดโค้ช (ดูอย่างเดียว + กดเช็คชื่อได้) เหมือนกันทั้งสองโหมดจอ -->
            <sheet-session-column
              :session="s" :absences="day.absences" :coach-map="coachMap"
              :readonly="coachMode" :my-coach-id="myCoachId" :checkin-mode="coachMode"
              :highlight-mine="coachMode && highlightMine" :only-mine="onlyMine"
              @assign="openAssignMenu($event, day.classdate)"
              @edit-group="openEditGroup($event.session, $event.subgroup, day.classdate)"
              @add-group="openAddGroup($event, day.classdate)"
              @edit-note="openNoteDialog($event, day.classdate)"
              @auto-group="openAutoDialog($event, day.classdate)"
              @checkin="doCheckin($event)"></sheet-session-column>
          </div>
        </div>
      </div>
    </div>
    </div><!-- /.scard gs-grid-card -->

    <!-- ตารางสอน Private (เฉพาะโหมดโค้ช) — ใช้ช่วงวัน/สัปดาห์เดียวกับกริดคลาสกลุ่มด้านบน
         โค้ชเห็นคาบ Private ของทุกคน (ตารางสอนรวม) แต่ไม่เห็นข้อมูลการเงิน; ไฮไลท์/กรอง "ของฉัน"
         เทียบ coachusername กับ user ที่ล็อกอิน. ซ่อนทั้งการ์ดเงียบ ๆ ถ้า backend ยังไม่เปิดสิทธิ์
         (403/404) เหมือนแพตเทิร์น coach-mode ของ fetchData -->
    <div v-if="coachMode && privateSupported" class="scard gs-grid-card gs-pv-card">
      <div class="gs-pv-title"><span class="mdi mdi-account-heart-outline"></span> {{ $t('grouping.privateTitle') }}</div>
      <div class="gs-pv-hint"><span class="mdi mdi-information-outline"></span> {{ $t('grouping.privateCheckinHint') }}</div>

      <div v-if="loading" class="gs-pv-list">
        <div v-for="i in 2" :key="i" class="id-skel" style="height:52px"></div>
      </div>

      <!-- day mode: รายการคาบของวันที่เลือก -->
      <div v-else-if="mode === 'day'" class="id-fade-in" :key="'pd' + selectedDate">
        <div v-if="privateForDate(selectedDate).length" class="gs-pv-list">
          <div v-for="b in privateForDate(selectedDate)" :key="b.privatebookingid" class="gs-pv-item"
            :class="{ 'gs-pv-mine': highlightMine && isMinePrivate(b), 'gs-pv-clickable': canCheckinPrivate(b) }"
            @click="onPrivateRowClick($event, b)">
            <div class="gs-pv-row1">
              <span class="gs-pv-time"><span class="mdi mdi-clock-outline"></span> {{ b.classtime }}</span>
              <span class="badge badge-info">{{ privateTypeLabel(b.privatetype) }}</span>
              <span class="badge badge-neutral">{{ $t('grouping.privateHours', { n: b.hours }) }}</span>
              <span v-if="Number(b.checkedin) === 1" class="badge badge-success">{{ $t('bookingList.menuCheckedInBadge') }}</span>
            </div>
            <div class="gs-pv-coach"><span class="mdi mdi-whistle-outline"></span> {{ coachDisplayName(b) }}<span v-if="highlightMine && isMinePrivate(b)" class="mdi mdi-star gs-pv-star"></span></div>
            <div v-if="b.price != null" class="gs-pv-money"><span class="mdi mdi-cash"></span> ฿{{ $money(b.price) }}<span class="badge" :class="Number(b.paid) === 1 ? 'badge-success' : 'badge-warning'">{{ Number(b.paid) === 1 ? $t('customerCourse.paid') : $t('customerCourse.unpaid') }}</span></div>
            <div class="gs-pv-students"><span class="mdi mdi-account-multiple-outline"></span> {{ privateStudentNames(b) }}</div>
            <div v-if="b.note" class="gs-pv-note">{{ b.note }}</div>
          </div>
        </div>
        <div v-else class="gs-dayempty">{{ $t('grouping.noPrivate') }}</div>
      </div>

      <!-- week mode: คอลัมน์ละวันเหมือนกริดสัปดาห์ด้านบน -->
      <div v-else class="tt-grid-wrap id-fade-in" :key="'pw' + weekStart">
        <div class="tt-row">
          <div v-for="d in weekDates" :key="d" class="gs-daycol" :class="{ today: d === todayStr }">
            <div class="gs-dayhead"><span class="strong">{{ dayHeadLabel(d) }}</span></div>
            <div v-if="privateForDate(d).length === 0" class="gs-dayempty">{{ $t('grouping.noPrivate') }}</div>
            <div v-for="b in privateForDate(d)" :key="b.privatebookingid" class="gs-pv-item"
              :class="{ 'gs-pv-mine': highlightMine && isMinePrivate(b), 'gs-pv-clickable': canCheckinPrivate(b) }"
              @click="onPrivateRowClick($event, b)">
              <div class="gs-pv-row1">
                <span class="gs-pv-time"><span class="mdi mdi-clock-outline"></span> {{ b.classtime }}</span>
                <span class="badge badge-info">{{ privateTypeLabel(b.privatetype) }}</span>
              </div>
              <div class="gs-pv-row1" style="margin-top:3px">
                <span class="badge badge-neutral">{{ $t('grouping.privateHours', { n: b.hours }) }}</span>
                <span v-if="Number(b.checkedin) === 1" class="badge badge-success">{{ $t('bookingList.menuCheckedInBadge') }}</span>
              </div>
              <div class="gs-pv-coach"><span class="mdi mdi-whistle-outline"></span> {{ coachDisplayName(b) }}<span v-if="highlightMine && isMinePrivate(b)" class="mdi mdi-star gs-pv-star"></span></div>
              <div class="gs-pv-students"><span class="mdi mdi-account-multiple-outline"></span> {{ privateStudentNames(b) }}</div>
              <div v-if="b.note" class="gs-pv-note">{{ b.note }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- assign menu (anchored to the clicked student row) — persistent + document handler กัน
         อาการกระพริบ-ปิดตอนคลิกเด็กคนใหม่ (แพตเทิร์นเดียวกับ BookingListAdmin หน้า Dashboard):
         Vuetify ไม่ปิดเองเมื่อคลิกนอกเมนู, ตัวจัดการ mousedown เป็นคนตัดสินใจ — คลิกแถวเด็กอื่น
         (.gsb-row.clickable) จะไม่ปิด แต่ re-anchor ไปเด็กใหม่แทน -->
    <v-menu v-model="assignMenuOpen" :target="assignMenuTarget" location="bottom start" offset="6"
      :close-on-content-click="false" :persistent="true" transition="scale-transition" origin="top left">
      <div class="popmenu">
        <div class="popmenu-head"><span class="popmenu-name">{{ assignStudentName }}</span></div>
        <button v-for="g in assignTargets" :key="g.subgroupid" class="popmenu-item" @click="doAssign(g.subgroupid)">
          <span class="pm-ico"><v-icon size="17" color="#6366f1">mdi-arrow-right-bold-circle-outline</v-icon></span>
          {{ $t('grouping.moveTo') }} {{ subgroupTitle(g) }}
        </button>
        <div v-if="assignCtx && assignCtx.currentSubgroupId != null" class="popmenu-divider"></div>
        <button v-if="assignCtx && assignCtx.currentSubgroupId != null" class="popmenu-item" @click="doUnassign">
          <span class="pm-ico"><v-icon size="17" color="#ef4444">mdi-close-circle-outline</v-icon></span>
          {{ $t('grouping.removeFromGroup') }}
        </button>
        <div v-if="assignTargets.length === 0 && (!assignCtx || assignCtx.currentSubgroupId == null)" class="popmenu-item" style="cursor:default;color:var(--c-text-muted)">
          {{ $t('grouping.noGroupYet') }}
        </div>
      </div>
    </v-menu>

    <!-- Private checkin quick menu (anchored to the clicked .gs-pv-item) — only ever opened from
         a row canCheckinPrivate() already allowed, but the endpoint re-checks server-side too.
         Same popup/confirm pattern as center/PrivateBookingList.vue's quick roster. -->
    <v-menu v-model="privateCheckinMenuOpen" :target="privateCheckinMenuTarget" location="bottom start" offset="8"
      min-width="200" max-width="260"
      :close-on-content-click="false" :persistent="true" transition="scale-transition" origin="top left">
      <div class="popmenu quick-menu">
        <div class="popmenu-head">
          <span class="popmenu-name">{{ selectedPrivateBooking ? (selectedPrivateBooking.classtime + ' · ' + coachDisplayName(selectedPrivateBooking)) : '—' }}</span>
          <span v-if="privateIsCheckedIn" class="badge badge-success" style="height:20px;font-size:11px">
            <v-icon size="12">mdi-check-decagram</v-icon>{{ $t('bookingList.menuCheckedInBadge') }}
          </span>
        </div>
        <button class="popmenu-item" @click="onChoosePrivateCheckin">
          <!-- ใช้ token แทนค่าสีตรง ๆ: ค่าใน :root คือ #b45309 / #047857 พอดีอยู่แล้ว โหมดสว่างจึง
               ไม่เปลี่ยนเลย แต่พื้นเมนูในโหมดมืดเป็น --c-surface สีเข้ม ค่าเดิมจะเหลือคอนทราสต์
               2.91 (เขียว) ซึ่งต่ำกว่าเกณฑ์ไอคอน 3.0 — token มีเฉดสว่างของโหมดมืดให้อยู่แล้ว -->
          <span class="pm-ico"><v-icon size="18" :color="privateIsCheckedIn ? 'var(--c-warning-tx)' : 'var(--c-success-tx)'">{{ privateIsCheckedIn ? 'mdi-undo-variant' : 'mdi-check-bold' }}</v-icon></span>
          {{ privateIsCheckedIn ? $t('bookingList.menuUndoCheckin') : $t('bookingList.menuCheckin') }}
        </button>
      </div>
    </v-menu>

    <!-- check-in / undo check-in confirmation for the Private card above -->
    <id-modal v-model="privateCheckinConfirmDialog" size="sm" persistent
      :icon="privateIsCheckedIn ? 'mdi-undo-variant' : 'mdi-check-circle'"
      :title="privateIsCheckedIn ? $t('bookingList.menuUndoCheckin') : $t('bookingList.menuCheckin')">
      <p style="margin:0">{{ privateIsCheckedIn
        ? $t('bookingMgmt.confirmUndoCheckin', { name: selectedPrivateBooking ? privateStudentNames(selectedPrivateBooking) : '' })
        : $t('bookingMgmt.confirmCheckin', { name: selectedPrivateBooking ? privateStudentNames(selectedPrivateBooking) : '' }) }}</p>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="privateCheckinConfirmDialog = false">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" @click="confirmPrivateCheckin"><span class="mdi mdi-check"></span> {{ $t('btn.ok') }}</button>
      </template>
    </id-modal>

    <!-- add / edit subgroup dialog -->
    <id-modal v-model="groupDialog" size="md" icon="mdi-account-group-outline"
      :title="groupForm.subgroupid ? $t('grouping.editGroup') : $t('grouping.addGroup')"
      :subtitle="groupDialogSubtitle" persistent>
      <div class="form-grid">
        <div class="field full"><label>{{ $t('grouping.coachesLabel') }}</label>
          <div class="gs-coachpick">
            <span v-for="c in groupDialogCoaches" :key="c.coachid" class="gs-coachopt" :class="{ on: groupForm.coachids.includes(c.coachid) }"
              @click="toggleGroupCoach(c.coachid)">
              <span class="id-check" :class="{ on: groupForm.coachids.includes(c.coachid) }"><span class="mdi mdi-check"></span></span>
              <span class="gs-coachdot" :style="{ background: c.color || '#8b5cf6' }"></span>{{ c.displayname }}
              <span v-if="Number(c.enableflag) === 0" class="badge badge-neutral" style="font-size:10.5px">{{ $t('grouping.disabledBadge') }}</span>
            </span>
            <span v-if="groupDialogCoaches.length === 0" style="font-size:12.5px;color:var(--c-text-muted)">{{ $t('grouping.noCoachYet') }}</span>
          </div>
        </div>
        <div class="field full"><label>{{ $t('grouping.groupName') }}</label>
          <input class="id-input" v-model="groupForm.subgroupname" :placeholder="$t('grouping.groupNamePh')"></div>
        <div class="field full"><label>{{ $t('grouping.groupRemark') }}</label>
          <textarea class="id-input id-textarea" rows="2" v-model="groupForm.remark" :placeholder="$t('grouping.groupRemarkPh')"></textarea></div>
      </div>

      <template v-if="groupForm.subgroupid && editingMembers.length">
        <div class="modal-sec mt"><span class="mdi mdi-account-switch"></span> {{ $t('grouping.moveAllTitle', { n: editingMembers.length }) }}</div>
        <div class="gs-moveall">
          <id-select v-model="moveTargetId" :options="moveTargetOptions" :placeholder="$t('grouping.moveAllTo')"></id-select>
          <button type="button" class="id-btn id-btn-ghost" :disabled="!moveTargetId" @click="moveAllMembers">
            <span class="mdi mdi-account-switch"></span> {{ $t('grouping.moveAllBtn') }}</button>
        </div>
      </template>

      <template #footer>
        <button v-if="groupForm.subgroupid" class="id-btn id-btn-ghost" style="margin-right:auto;color:var(--c-error)" @click="groupDeleteDialog = true">
          <span class="mdi mdi-trash-can-outline"></span> {{ $t('grouping.deleteGroup') }}</button>
        <button class="id-btn id-btn-ghost" @click="groupDialog = false">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" :disabled="!groupFormValid" @click="saveGroup">
          <span class="mdi mdi-content-save"></span> {{ $t('btn.save') }}</button>
      </template>
    </id-modal>

    <!-- delete subgroup confirm -->
    <id-modal v-model="groupDeleteDialog" size="sm" icon="mdi-trash-can-outline" :title="$t('common.confirmDeleteTitle')" persistent>
      <p style="margin:0">{{ $t('grouping.confirmDeleteGroup', { name: editingGroupTitle }) }}</p>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="groupDeleteDialog = false">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" style="background:var(--c-error)" @click="deleteGroup">
          <span class="mdi mdi-trash-can-outline"></span> {{ $t('btn.ok') }}</button>
      </template>
    </id-modal>

    <!-- session note dialog -->
    <id-modal v-model="noteDialog" size="md" icon="mdi-note-text-outline" :title="$t('grouping.sessionNote')" :subtitle="noteForm.label" persistent>
      <div class="form-grid">
        <div class="field full">
          <textarea class="id-input id-textarea" rows="3" v-model="noteForm.remark" :placeholder="$t('grouping.sessionNotePh')"></textarea>
        </div>
      </div>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="noteDialog = false">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" @click="saveNote"><span class="mdi mdi-content-save"></span> {{ $t('btn.save') }}</button>
      </template>
    </id-modal>

    <!-- day note dialog -->
    <id-modal v-model="dayNoteDialog" size="md" icon="mdi-notebook-outline" :title="$t('grouping.dayNote')" :subtitle="dayNoteForm.label" persistent>
      <div class="form-grid">
        <div class="field full">
          <textarea class="id-input id-textarea" rows="3" v-model="dayNoteForm.remark" :placeholder="$t('grouping.dayNotePh')"></textarea>
        </div>
      </div>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="dayNoteDialog = false">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" @click="saveDayNote"><span class="mdi mdi-content-save"></span> {{ $t('btn.save') }}</button>
      </template>
    </id-modal>

    <!-- ── พรีวิวจัดกลุ่มอัตโนมัติ — ยังไม่เขียน DB จนกว่าจะกดยืนยัน ────────────────
         autoPlans = 1 รายการต่อคาบ (กดจากคอลัมน์ = 1 คาบ, กดจากทูลบาร์ = ทุกคาบที่มีกฎในวันนั้น) -->
    <id-modal v-model="autoDialog" size="lg" icon="mdi-auto-fix" :title="$t('grouping.autoPreviewTitle')"
      :subtitle="autoSubtitle" persistent>
      <div class="gs-auto-modes">
        <button type="button" class="id-btn" :class="autoMode === 'unassigned_only' ? 'id-btn-primary' : 'id-btn-ghost'"
          @click="setAutoMode('unassigned_only')">{{ $t('grouping.autoModeUnassigned') }}</button>
        <button type="button" class="id-btn" :class="autoMode === 'rebuild' ? 'id-btn-primary' : 'id-btn-ghost'"
          @click="setAutoMode('rebuild')">{{ $t('grouping.autoModeRebuild') }}</button>
      </div>
      <div class="gs-auto-modehint">{{ autoMode === 'rebuild' ? $t('grouping.autoModeRebuildHint') : $t('grouping.autoModeUnassignedHint') }}</div>

      <div v-if="autoLoading" class="gs-auto-list">
        <div v-for="i in 2" :key="i" class="id-skel" style="height:72px"></div>
      </div>

      <div v-else class="gs-auto-list id-fade-in" :key="autoMode">
        <div v-for="p in autoPlans" :key="p.classid" class="gs-auto-sess">
          <div v-if="autoPlans.length > 1 || p.error" class="gs-auto-sesshead">
            <span class="mdi mdi-clock-outline"></span> {{ p.class_label }}
            <span v-if="p.rulename" class="badge badge-neutral">{{ p.rulename }}</span>
          </div>
          <div v-if="p.error" class="gs-auto-err"><span class="mdi mdi-information-outline"></span> {{ p.error }}</div>
          <template v-else>
            <div v-for="b in p.buckets" :key="b.bucketname" class="gs-auto-bucket">
              <div class="gs-auto-bhead">
                <span class="strong">{{ b.bucketname }}</span>
                <span class="gs-auto-count">{{ b.students.length }} {{ $t('grouping.personUnit') }}</span>
                <span class="badge" :class="b.existingSubgroupId ? 'badge-neutral' : 'badge-info'">
                  {{ b.existingSubgroupId ? $t('grouping.autoReuseGroup') : $t('grouping.autoNewGroup') }}</span>
                <span v-for="cid in b.coachids" :key="cid" class="gs-auto-coach">
                  <span class="gs-coachdot" :style="{ background: (coachMap[cid] && coachMap[cid].color) || '#8b5cf6' }"></span>
                  {{ (coachMap[cid] && coachMap[cid].displayname) || ('#' + cid) }}</span>
              </div>
              <div class="gs-auto-names">{{ b.students.map((s) => s.name).join(' · ') }}</div>
            </div>
            <div v-if="p.buckets.length === 0" class="gs-auto-none">{{ $t('grouping.autoNothingToDo') }}</div>
            <div v-if="p.skipped.length" class="gs-auto-skip">
              <div class="gs-auto-skiphead"><span class="mdi mdi-alert-outline"></span>
                {{ $t('grouping.autoSkipTitle', { n: p.skipped.length }) }}</div>
              <div v-for="s in p.skipped" :key="s.reservationid" class="gs-auto-skiprow">
                {{ s.name }} — {{ s.reason === 'nodata' ? skipReasonNoData(p.basis) : $t('grouping.autoSkipOutOfRange') }}
              </div>
            </div>
          </template>
        </div>
        <div v-if="autoPlans.length === 0" class="gs-auto-none">{{ $t('grouping.autoNothingToDo') }}</div>
      </div>

      <template #footer>
        <span class="gs-auto-total">{{ $t('grouping.autoTotal', { n: autoTotalStudents, g: autoTotalGroups }) }}</span>
        <button class="id-btn id-btn-ghost" @click="autoDialog = false">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" :disabled="autoLoading || autoTotalStudents === 0" @click="confirmAuto">
          <span class="mdi mdi-check"></span> {{ $t('grouping.autoApplyBtn') }}</button>
      </template>
    </id-modal>

    <!-- ยืนยันซ้ำสำหรับโหมด "จัดใหม่ทั้งคาบ" — โหมดนี้ล้างกลุ่มที่จัดมือไว้แล้ว จึงไม่ให้พลาดกดผ่าน -->
    <id-modal v-model="autoRebuildConfirm" size="sm" icon="mdi-alert-outline" :title="$t('grouping.autoRebuildConfirmTitle')" persistent>
      <p style="margin:0">{{ $t('grouping.autoRebuildConfirmBody') }}</p>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="autoRebuildConfirm = false">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" style="background:var(--c-error)" @click="applyAuto">
          <span class="mdi mdi-auto-fix"></span> {{ $t('btn.ok') }}</button>
      </template>
    </id-modal>

    <!-- ── ตั้งค่ากฎจัดกลุ่ม: รายการชุดกฎ (แก้ทีละชุดใน dialog ถัดไป — แพตเทิร์นเดียวกับรายชื่อโค้ช) ── -->
    <id-modal v-model="ruleDialog" size="lg" icon="mdi-tune-variant" :title="$t('grouping.ruleTitle')" :subtitle="$t('grouping.ruleSub')">
      <div v-if="ruleLoading" class="gs-auto-list">
        <div v-for="i in 2" :key="i" class="id-skel" style="height:64px"></div>
      </div>
      <div v-else class="gs-rulelist">
        <div v-for="r in rules" :key="r.ruleid" class="gs-rulerow" :class="{ off: Number(r.enableflag) === 0 }">
          <div style="flex:1;min-width:0">
            <div class="strong" style="font-size:13.5px">{{ r.rulename }}
              <span class="badge badge-neutral">{{ r.course_shortname || ('#' + r.courseid) }}</span>
              <span class="badge badge-info">{{ r.basis === 'age' ? $t('grouping.basisAge') : $t('grouping.basisLevel') }}</span>
              <span v-if="Number(r.enableflag) === 0" class="badge badge-neutral">{{ $t('grouping.disabledBadge') }}</span>
            </div>
            <div style="font-size:11.5px;color:var(--c-text-muted);margin-top:2px">
              {{ r.buckets.map((b) => b.bucketname + ' (' + rangeLabel(b) + ')').join(' · ') }}
            </div>
          </div>
          <span class="mdi mdi-pencil gs-coachact" v-hint="$t('grouping.ruleEdit')" @click="openRuleEdit(r)"></span>
          <span class="mdi mdi-trash-can-outline gs-coachact" style="color:var(--c-error)" v-hint="$t('grouping.ruleDelete')" @click="askDeleteRule(r)"></span>
        </div>
        <div v-if="rules.length === 0" class="id-empty" style="padding:18px">
          <div class="mdi mdi-tune-variant"></div>
          <div style="margin-top:8px">{{ $t('grouping.ruleNone') }}</div>
        </div>
      </div>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="ruleDialog = false">{{ $t('common.close') }}</button>
        <button class="id-btn id-btn-primary" :disabled="ruleCoursesAvailable.length === 0" @click="openRuleEdit(null)">
          <span class="mdi mdi-plus"></span> {{ $t('grouping.ruleAdd') }}</button>
      </template>
    </id-modal>

    <!-- แก้ไข/สร้างชุดกฎ 1 ชุด -->
    <id-modal v-model="ruleEditDialog" size="lg" icon="mdi-tune-variant"
      :title="ruleForm.ruleid ? $t('grouping.ruleEdit') : $t('grouping.ruleAdd')" persistent>
      <div class="form-grid">
        <div class="field"><label>{{ $t('grouping.ruleCourse') }} <span class="req">*</span></label>
          <id-select v-model="ruleForm.courseid" :options="ruleCourseOptions" :placeholder="$t('common.select')"></id-select></div>
        <div class="field"><label>{{ $t('grouping.ruleBasis') }} <span class="req">*</span></label>
          <id-select v-model="ruleForm.basis" :options="basisOptions"></id-select></div>
        <div class="field full"><label>{{ $t('grouping.ruleName') }} <span class="req">*</span></label>
          <input class="id-input" v-model="ruleForm.rulename" :placeholder="$t('grouping.ruleNamePh')"></div>
      </div>

      <div class="modal-sec mt"><span class="mdi mdi-format-list-group"></span> {{ $t('grouping.ruleBuckets') }}</div>
      <div class="gs-bucketlist">
        <div v-for="(b, i) in ruleForm.buckets" :key="i" class="gs-bucket">
          <div class="gs-bucket-r1">
            <input class="id-input" v-model="b.bucketname" :placeholder="$t('grouping.ruleBucketNamePh')">
            <span class="mdi mdi-trash-can-outline gs-coachact" style="color:var(--c-error)" @click="ruleForm.buckets.splice(i, 1)"></span>
          </div>
          <div class="gs-bucket-r2">
            <div class="field"><label>{{ $t('grouping.ruleMin') }}</label>
              <input class="id-input" type="number" step="0.5" v-model="b.minval" :placeholder="$t('grouping.ruleOpenEnd')"></div>
            <div class="field"><label>{{ $t('grouping.ruleMax') }}</label>
              <input class="id-input" type="number" step="0.5" v-model="b.maxval" :placeholder="$t('grouping.ruleOpenEnd')"></div>
            <div class="field"><label>{{ $t('grouping.ruleMaxPerson') }}</label>
              <input class="id-input" type="number" min="1" v-model="b.maxperson" :placeholder="$t('grouping.ruleNoLimit')"></div>
          </div>
          <div class="gs-bucket-r3">
            <label class="gs-bucket-lbl">{{ $t('grouping.ruleDefaultCoaches') }}</label>
            <div class="gs-coachpick">
              <span v-for="c in activeCoaches" :key="c.coachid" class="gs-coachopt sm" :class="{ on: b.coachids.includes(c.coachid) }"
                @click="toggleBucketCoach(b, c.coachid)">
                <span class="gs-coachdot" :style="{ background: c.color || '#8b5cf6' }"></span>{{ c.displayname }}
              </span>
              <span v-if="activeCoaches.length === 0" style="font-size:12px;color:var(--c-text-muted)">{{ $t('grouping.noCoachYet') }}</span>
            </div>
          </div>
        </div>
        <button type="button" class="id-btn id-btn-ghost gsc-addbtn" @click="addBucket">
          <span class="mdi mdi-plus"></span> {{ $t('grouping.ruleAddBucket') }}</button>
      </div>
      <div class="gs-rulehint"><span class="mdi mdi-information-outline"></span> {{ $t('grouping.ruleHint') }}</div>

      <template #footer>
        <button class="id-btn id-btn-ghost" @click="ruleEditDialog = false">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" :disabled="!ruleFormValid" @click="saveRule">
          <span class="mdi mdi-content-save"></span> {{ $t('btn.save') }}</button>
      </template>
    </id-modal>

    <!-- ยืนยันลบชุดกฎ -->
    <id-modal v-model="ruleDeleteDialog" size="sm" icon="mdi-trash-can-outline" :title="$t('common.confirmDeleteTitle')" persistent>
      <p style="margin:0">{{ $t('grouping.ruleConfirmDelete', { name: ruleToDelete ? ruleToDelete.rulename : '' }) }}</p>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="ruleDeleteDialog = false">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" style="background:var(--c-error)" @click="deleteRule">
          <span class="mdi mdi-trash-can-outline"></span> {{ $t('btn.ok') }}</button>
      </template>
    </id-modal>

    <!-- บันทึกการลา/ไม่มาสอนของโค้ช — แสดงทุกฟิลด์ไว้เลย, ฟิลด์ที่ flow ไม่ใช้ = disable (ไม่เด้งเข้าออก).
         ลำดับ: โค้ช → ประเภทการลา → วันที่ → เต็ม/ครึ่งวัน → เหตุผล → สร้างใบลา (สอดคล้องกับหน้ายื่นใบลา) -->
    <id-modal v-model="absenceDialog" size="md" icon="mdi-account-off" :title="$t('grouping.absenceTitle')" persistent>
      <div class="form-grid">
        <div class="field full"><label>{{ $t('grouping.absenceCoach') }} <span class="req">*</span></label>
          <id-select v-model="absenceForm.coachid" :options="absenceCoachOptions" :placeholder="$t('common.select')"></id-select>
          <small v-if="absenceCoach" style="display:block;margin-top:2px;color:var(--c-text-muted)">
            {{ absenceCoachFulltime ? $t('grouping.employmentFulltime') : $t('grouping.employmentParttime') }}
            — {{ absenceCoachFulltime ? $t('grouping.countAsLeaveHint') : $t('grouping.parttimeNoQuota') }}
          </small>
        </div>
        <div class="field full"><label>{{ $t('grouping.leaveTypeLabel') }} <span v-if="absenceTypeEnabled" class="req">*</span></label>
          <id-select v-model="absenceForm.leavetype" :options="absenceLeaveTypeOptions" :disabled="!absenceTypeEnabled" :placeholder="$t('common.select')"></id-select></div>
        <div class="field full"><label>{{ $t('grouping.absenceDate') }} <span class="req">*</span></label>
          <id-date v-model="absenceForm.absencedate" :placeholder="$t('common.selectDate')"></id-date></div>
        <div class="field full"><label>{{ $t('grouping.dayPortionLabel') }}</label>
          <id-select v-model="absenceForm.dayportion" :options="dayPortionOptions"></id-select></div>
        <div class="field full"><label>{{ $t('grouping.absenceReason') }}</label>
          <input class="id-input" v-model="absenceForm.reason" :placeholder="$t('grouping.absenceReasonPh')"></div>
        <div class="field full">
          <label class="gs-coachopt" :class="{ 'gs-opt-disabled': !absenceCoachFulltime }" style="padding:0"
            @click="absenceCoachFulltime && (absenceForm.createLeave = !absenceForm.createLeave)">
            <span class="id-check" :class="{ on: absenceCoachFulltime && absenceForm.createLeave }"><span class="mdi mdi-check"></span></span>
            {{ $t('grouping.createLeaveLabel') }}
          </label>
          <small style="display:block;margin-top:2px;color:var(--c-text-muted)">{{ $t('grouping.createLeaveHint') }}</small>
        </div>
      </div>
      <template #footer>
        <button v-if="absenceForm.existing" class="id-btn id-btn-ghost" style="margin-right:auto;color:var(--c-error)" @click="saveAbsence(false)">
          <span class="mdi mdi-account-check"></span> {{ $t('grouping.absenceCancelBtn') }}</button>
        <button class="id-btn id-btn-ghost" @click="absenceDialog = false">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" :disabled="!absenceFormValid" @click="saveAbsence(true)">
          <span class="mdi mdi-content-save"></span> {{ $t('btn.save') }}</button>
      </template>
    </id-modal>

    <!-- coach roster dialog -->
    <id-modal v-model="coachDialog" size="lg" icon="mdi-account-cog-outline" :title="$t('grouping.coachManageTitle')" :subtitle="$t('grouping.coachManageSub')">
      <div class="gs-coachlist">
        <div v-for="c in coachList" :key="c.coachid" class="gs-coachrow" :class="{ off: Number(c.enableflag) === 0 }">
          <span class="gs-coachdot lg" :style="{ background: c.color || '#8b5cf6' }"></span>
          <div style="flex:1;min-width:0">
            <div class="strong" style="font-size:13.5px">{{ c.displayname }}
              <span v-if="c.employment_type === 'parttime'" class="badge badge-neutral" style="font-size:10.5px">{{ $t('grouping.employmentParttime') }}</span>
              <span v-if="Number(c.enableflag) === 0" class="badge badge-neutral" style="font-size:10.5px">{{ $t('grouping.disabledBadge') }}</span>
            </div>
            <div style="font-size:11.5px;color:var(--c-text-muted)">
              <template v-if="c.coachusername">{{ c.coachusername }}<span v-if="c.firstname"> ({{ c.firstname }} {{ c.lastname }})</span></template>
              <template v-else>{{ $t('grouping.notLinkedBadge') }}</template>
            </div>
          </div>
          <span class="mdi mdi-pencil gs-coachact" v-hint="$t('grouping.editCoach')" @click="openCoachEdit(c)"></span>
          <span v-if="Number(c.enableflag) !== 0" class="mdi mdi-account-off gs-coachact" style="color:var(--c-error)"
            v-hint="$t('grouping.coachDisable')" @click="askDisableCoach(c)"></span>
        </div>
        <div v-if="coachList.length === 0" class="id-empty" style="padding:18px">
          <div class="mdi mdi-account-question-outline"></div>
          <div style="margin-top:8px">{{ $t('grouping.noCoachYet') }}</div>
        </div>
      </div>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="coachDialog = false">{{ $t('common.close') }}</button>
        <button class="id-btn id-btn-primary" @click="openCoachEdit(null)"><span class="mdi mdi-plus"></span> {{ $t('grouping.addCoach') }}</button>
      </template>
    </id-modal>

    <!-- add / edit coach dialog -->
    <id-modal v-model="coachEditDialog" size="md" icon="mdi-account-cog-outline"
      :title="coachForm.coachid ? $t('grouping.editCoach') : $t('grouping.addCoach')" persistent>
      <div class="form-grid">
        <div class="field"><label>{{ $t('grouping.coachName') }} <span class="req">*</span></label>
          <input class="id-input" v-model="coachForm.displayname" placeholder="PP / Mew / P'Big"></div>
        <div class="field"><label>{{ $t('grouping.coachSort') }}</label>
          <input class="id-input" type="number" v-model="coachForm.sortorder"></div>
        <div class="field full"><label>{{ $t('grouping.coachColor') }}</label>
          <div class="gs-palette">
            <span v-for="col in palette" :key="col" class="gs-swatch" :style="{ background: col }"
              :class="{ on: coachForm.color === col }" @click="coachForm.color = col"></span>
          </div>
        </div>
        <div class="field full"><label>{{ $t('grouping.coachLink') }}</label>
          <id-select v-model="coachForm.coachusername" searchable :options="staffOptions" :placeholder="$t('grouping.coachLinkNone')"></id-select>
          <small style="color:var(--c-text-muted)">{{ $t('grouping.coachLinkHint') }}</small></div>
        <div class="field full"><label>{{ $t('grouping.employmentType') }}</label>
          <id-select v-model="coachForm.employment_type" :options="employmentTypeOptions"></id-select>
          <small style="color:var(--c-text-muted)">{{ $t('grouping.employmentTypeHint') }}</small></div>
      </div>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="coachEditDialog = false">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" :disabled="!coachForm.displayname || !coachForm.displayname.trim()" @click="saveCoach">
          <span class="mdi mdi-content-save"></span> {{ $t('btn.save') }}</button>
      </template>
    </id-modal>

    <!-- disable coach confirm -->
    <id-modal v-model="coachDisableDialog" size="sm" icon="mdi-account-off" :title="$t('grouping.coachDisable')" persistent>
      <p style="margin:0">{{ $t('grouping.confirmDisableCoach', { name: coachToDisable ? coachToDisable.displayname : '' }) }}</p>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="coachDisableDialog = false">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" style="background:var(--c-error)" @click="disableCoach">
          <span class="mdi mdi-account-off"></span> {{ $t('btn.ok') }}</button>
      </template>
    </id-modal>

  </div>
</template>

<script>
import axios from 'axios'
import live from '@/utils/live'
import presence from '@/utils/presence'
import moment from 'moment'
import { mapGetters } from 'vuex'
import { formatDateIntl } from '@/utils/locale'
import { togglePrivateBookingCheckin } from '@/utils/privateBookingCheckin'
import SheetSessionColumn from './SheetSessionColumn.vue'

// ชุดสีหัวบล็อกโค้ชให้เลือก (โทนพาสเทลเข้มพอให้ตัดกับพื้นได้ทุกธีม)
const COACH_PALETTE = ['#8b5cf6', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#14b8a6', '#6366f1', '#a16207', '#64748b']

export default {
  components: { SheetSessionColumn },
  props: {
    // true = หน้าโค้ช (state 'coachschedule'): read-only + ไฮไลต์กลุ่มของฉัน
    coachMode: { type: Boolean, default: false },
    // 'YYYY-MM-DD' — เปิดหน้าที่วันนี้แทนวันนี้ ใช้ตอนกดการ์ดแจ้งเตือน (โค้ชต้องเห็นคาบที่ถูกแจ้ง
    // ทันที ไม่ใช่เปิดหน้าเปล่าของวันนี้แล้วให้ไปกดหาวันเอง) · null = ใช้วันนี้ตามเดิม
    initialDate: { type: String, default: null },
  },
  emits: ['onErrorHandler', 'onInfoHandler', 'onLoading', 'onClickChangeState'],
  data: () => ({
    viewers: [],       // สตาฟคนอื่นที่เปิดจอจัดกลุ่มอยู่ (โหมดโค้ชไม่ใช้)
    offPresence: null,
    presenceToken: 0,
    mode: 'day',
    selectedDate: moment().format('YYYY-MM-DD'),
    days: [],
    coaches: [],
    myCoachId: null,
    // คาบสอน Private ของช่วงที่แสดง (โหมดโค้ช) — backend ส่งเฉพาะคาบของโค้ชคนนี้ ไม่มีข้อมูลการเงิน
    privateBookings: [],
    // false = backend ตอบ 403/404 (ยังไม่เปิดสิทธิ์/ไม่มี endpoint) → ซ่อนการ์ด Private เงียบ ๆ
    privateSupported: true,
    // เช็คชื่อ Private จากการ์ดนี้ — เมนูป๊อปอัพ + ยืนยัน แพตเทิร์นเดียวกับ PrivateBookingList.vue
    privateCheckinMenuOpen: false,
    privateCheckinMenuTarget: null,
    selectedPrivateBooking: null,
    privateCheckinConfirmDialog: false,
    loading: false,
    highlightMine: true,
    onlyMine: false,
    palette: COACH_PALETTE,

    assignMenuOpen: false,
    assignMenuTarget: null,
    assignCtx: null, // { session, student, currentSubgroupId, classdate }

    groupDialog: false,
    groupDeleteDialog: false,
    groupForm: { subgroupid: null, classid: null, classdate: null, classtime: '', subgroupname: '', coachids: [], remark: '' },
    editingSubgroup: null,
    editingSession: null,
    moveTargetId: null,

    noteDialog: false,
    noteForm: { classid: null, classdate: null, label: '', remark: '' },

    dayNoteDialog: false,
    dayNoteForm: { groupdate: null, label: '', remark: '' },

    absenceDialog: false,
    absenceForm: { coachid: null, absencedate: null, dayportion: 'full', reason: '', existing: false, createLeave: true, leavetype: 'sick', originalDate: null },

    // จัดกลุ่มอัตโนมัติ — autoPlans: 1 รายการต่อคาบ { classid, classdate, class_label, rulename, basis,
    // buckets, skipped } หรือ { classid, class_label, error } เมื่อคาบนั้นจัดไม่ได้
    autoDialog: false,
    autoRebuildConfirm: false,
    autoLoading: false,
    autoMode: 'unassigned_only',
    autoPlans: [],
    autoTargets: [], // [{ classid, classdate, class_label }] — ใช้ยิงพรีวิวซ้ำเมื่อสลับโหมด

    ruleDialog: false,
    ruleEditDialog: false,
    ruleDeleteDialog: false,
    ruleLoading: false,
    rules: [],
    ruleCourses: [],
    ruleToDelete: null,
    ruleForm: { ruleid: null, courseid: '', rulename: '', basis: 'age', enableflag: 1, sortorder: 0, buckets: [] },

    coachDialog: false,
    coachEditDialog: false,
    coachDisableDialog: false,
    coachList: [],
    coachToDisable: null,
    coachForm: { coachid: null, displayname: '', color: '', sortorder: 0, coachusername: '', employment_type: 'fulltime' },
    staffOptions: [],

  }),
  computed: {
    ...mapGetters({ token: 'getToken' }),
    todayStr() { return moment().format('YYYY-MM-DD') },
    weekStart() { return moment(this.selectedDate, 'YYYY-MM-DD').startOf('isoWeek').format('YYYY-MM-DD') },
    weekLabel() {
      const start = this.weekStart
      const end = moment(start, 'YYYY-MM-DD').add(6, 'days').format('YYYY-MM-DD')
      return `${this.$formatShortDate(start)} – ${this.$formatShortDate(end)}`
    },
    dayData() { return this.days.length ? this.days[0] : null },
    // username ของผู้ล็อกอิน — ใช้เทียบว่าคาบ Private ไหนเป็น "ของฉัน" (private ผูกกับ coachusername)
    // อ่านจาก localStorage เหมือนทั้งแอป (App.vue/EditProfile/Home) — เดิมเพราะ Vuex getUserdata
    // เชื่อถือไม่ได้ (mutation destructure `user` แต่ action ส่ง `userdata`) ตอนนี้ store แก้แล้ว
    // `$store.getters.getUserdata` ใช้ได้จริง แต่คงไว้แบบนี้ก่อนเพื่อให้เหมือนหน้าอื่น ๆ
    myUsername() {
      try {
        const ud = JSON.parse(localStorage.getItem('userdata') || 'null')
        return (ud && ud.username) || null
      } catch (e) { return null }
    },
    // usertype ของผู้ล็อกอิน ('0' head / '1' admin / '2' coach) — ใช้ตัดสินใจว่าเช็คชื่อ Private
    // ได้ทุกคาบไหม อ่านจาก localStorage แบบเดียวกับ myUsername ด้านบน (เหตุผลเดียวกัน)
    myUsertype() {
      try {
        const ud = JSON.parse(localStorage.getItem('userdata') || 'null')
        return (ud && String(ud.usertype)) || null
      } catch (e) { return null }
    },
    // head/admin เช็คชื่อคาบ Private ของใครก็ได้ — เป็นแค่ UX gate ฝั่ง frontend (ซ่อน/โชว์ปุ่ม),
    // การกันจริงอยู่ที่ backend (canCheckinPrivateBooking, server.js)
    canCheckinAnyPrivate() {
      return this.myUsertype === '0' || this.myUsertype === '1'
    },
    privateIsCheckedIn() {
      const b = this.selectedPrivateBooking
      if (!b) return false
      return Number(b.checkedin) === 1
    },
    // 7 วันของสัปดาห์ที่แสดง — คอลัมน์กริด Private โหมดสัปดาห์
    weekDates() {
      return Array.from({ length: 7 }, (_, i) => moment(this.weekStart, 'YYYY-MM-DD').add(i, 'days').format('YYYY-MM-DD'))
    },
    coachMap() {
      const m = {}
      for (const c of this.coaches) m[c.coachid] = c
      return m
    },
    activeCoaches() { return this.coaches.filter((c) => Number(c.enableflag) !== 0) },
    // dialog แก้กลุ่ม: โชว์โค้ชที่ถูกปิดใช้งานด้วยถ้ายังติดอยู่ใน coachids ของกลุ่ม —
    // ไม่งั้นผู้จัดการมองไม่เห็นและเอาออกจากกลุ่มไม่ได้
    groupDialogCoaches() {
      return this.coaches.filter((c) => Number(c.enableflag) !== 0 || this.groupForm.coachids.includes(c.coachid))
    },
    absenceCoachOptions() {
      return this.coaches
        .filter((c) => Number(c.enableflag) !== 0 || c.coachid === this.absenceForm.coachid)
        .map((c) => ({ value: c.coachid, label: c.displayname }))
    },
    dayAbsenceChips() { return this.dayData ? this.absenceChipsFor(this.dayData) : [] },
    assignStudentName() {
      if (!this.assignCtx) return ''
      return String(this.assignCtx.student.name || '')
        .replace('(1)', '').replace('(red)', '').replace('(green)', '').replace('(blue)', '')
        .replace('(yellow)', '').replace('(pink)', '').replace('(pay)', '').replace('(blind)', '').trim()
    },
    assignTargets() {
      if (!this.assignCtx) return []
      return this.assignCtx.session.subgroups.filter((g) => g.subgroupid !== this.assignCtx.currentSubgroupId)
    },
    groupDialogSubtitle() {
      if (!this.editingSession || !this.groupForm.classdate) return ''
      return `${this.editingSession.class_label} • ${this.$formatShortDate(this.groupForm.classdate)}`
    },
    groupFormValid() {
      return this.groupForm.coachids.length > 0 || (this.groupForm.subgroupname && this.groupForm.subgroupname.trim() !== '')
    },
    editingMembers() { return this.editingSubgroup ? this.editingSubgroup.members : [] },
    editingGroupTitle() { return this.editingSubgroup ? this.subgroupTitle(this.editingSubgroup) : '' },
    moveTargetOptions() {
      if (!this.editingSession || !this.editingSubgroup) return []
      return this.editingSession.subgroups
        .filter((g) => g.subgroupid !== this.editingSubgroup.subgroupid)
        .map((g) => ({ value: g.subgroupid, label: this.subgroupTitle(g) }))
    },
    absenceFormValid() {
      if (!this.absenceForm.coachid || !this.absenceForm.absencedate) return false
      if (this.absenceTypeEnabled && !this.absenceForm.leavetype) return false
      return true
    },
    // โค้ชที่เลือกใน dialog ลา
    absenceCoach() { return this.coachMap[this.absenceForm.coachid] || null },
    absenceCoachFulltime() {
      // ค่า employment_type ที่ไม่ใช่ 'parttime' ให้ถือเป็น full-time (ค่า default)
      return !!this.absenceCoach && this.absenceCoach.employment_type !== 'parttime'
    },
    // ประเภทการลาจะใช้ (required) เฉพาะเมื่อโค้ช full-time + ติ๊กสร้างใบลา
    absenceTypeEnabled() {
      return this.absenceCoachFulltime && this.absenceForm.createLeave
    },
    // ประเภทการลาสำหรับ dialog ผู้จัดการ (รวม unpaid ที่ไม่ตัดโควต้า)
    absenceLeaveTypeOptions() {
      return ['sick', 'personal', 'vacation', 'unpaid'].map((t) => ({ value: t, label: this.$t('grouping.leaveType_' + t) }))
    },
    // เต็มวัน / ครึ่งเช้า / ครึ่งบ่าย (ใช้ทั้ง dialog นี้และหน้ายื่นใบลา)
    dayPortionOptions() {
      return ['full', 'morning', 'afternoon'].map((p) => ({ value: p, label: this.$t('grouping.dayPortion_' + p) }))
    },
    // วันนี้มีคาบที่ตั้งกฎไว้ไหม — ซ่อนปุ่ม "จัดกลุ่มอัตโนมัติทั้งวัน" ถ้าไม่มีเลย
    dayHasRuleSessions() {
      return !!this.dayData && this.dayData.sessions.some((s) => s.hasRule)
    },
    autoSubtitle() {
      if (this.autoTargets.length === 0) return ''
      if (this.autoTargets.length === 1) {
        const t = this.autoTargets[0]
        const rule = this.autoPlans.length ? this.autoPlans[0].rulename : ''
        return `${t.class_label} • ${this.$formatShortDate(t.classdate)}${rule ? ' • ' + rule : ''}`
      }
      return `${this.$formatFullDate(this.autoTargets[0].classdate)} • ${this.$t('grouping.autoSessionCount', { n: this.autoTargets.length })}`
    },
    autoTotalStudents() {
      return this.autoPlans.reduce((n, p) => n + (p.buckets || []).reduce((m, b) => m + b.students.length, 0), 0)
    },
    autoTotalGroups() {
      return this.autoPlans.reduce((n, p) => n + (p.buckets || []).length, 0)
    },
    basisOptions() {
      return [
        { value: 'age', label: this.$t('grouping.basisAge') },
        { value: 'level', label: this.$t('grouping.basisLevel') },
      ]
    },
    // 1 คอร์ส 1 ชุดกฎ — คอร์สที่มีชุดแล้วจะไม่ให้เลือกซ้ำ (ยกเว้นคอร์สของชุดที่กำลังแก้อยู่)
    ruleCoursesAvailable() {
      const taken = new Set(this.rules.map((r) => r.courseid))
      return this.ruleCourses.filter((c) => !taken.has(c.courseid))
    },
    ruleCourseOptions() {
      const taken = new Set(this.rules.filter((r) => r.ruleid !== this.ruleForm.ruleid).map((r) => r.courseid))
      return this.ruleCourses
        .filter((c) => !taken.has(c.courseid))
        .map((c) => ({ value: c.courseid, label: `${c.course_shortname} — ${c.coursename}` }))
    },
    ruleFormValid() {
      const f = this.ruleForm
      if (!f.courseid || !f.rulename || !f.rulename.trim() || f.buckets.length === 0) return false
      return f.buckets.every((b) => b.bucketname && b.bucketname.trim()
        && !(String(b.minval).trim() === '' && String(b.maxval).trim() === ''))
    },
    employmentTypeOptions() {
      return [
        { value: 'fulltime', label: this.$t('grouping.employmentFulltime') },
        { value: 'parttime', label: this.$t('grouping.employmentParttime') },
      ]
    },
  },
  watch: {
    // App.vue ใช้ component นี้กับ 2 state (groupingmanager / coachschedule) — ถ้า Vue reuse
    // instance ตอนสลับ state ให้รีเซ็ตและโหลดใหม่ตามโหมดปัจจุบัน
    coachMode() {
      this.mode = 'day'
      this.onlyMine = false
      this.fetchData()
    },
  },
  async created() {
    // ตั้งวันก่อนโหลดข้อมูล ไม่งั้นจะยิง fetch ของ "วันนี้" ทิ้งไปหนึ่งรอบแล้วค่อยยิงวันที่ถูก
    // (รับเฉพาะรูปแบบ YYYY-MM-DD ที่ backend ส่งมา — ค่าผิดรูปให้ตกกลับไปใช้วันนี้ตามเดิม)
    if (this.initialDate && /^\d{4}-\d{2}-\d{2}$/.test(this.initialDate)) {
      this.selectedDate = this.initialDate
    }
    await this.fetchData()
  },
  mounted() {
    document.addEventListener('mousedown', this.onDocumentMousedown, true)
    document.addEventListener('keydown', this.onDocumentKeydown)
    // 🔑 ต่อสายเฉพาะ **coach-mode** (ตารางสอนโค้ช = อ่านอย่างเดียว) เท่านั้น
    // โหมดจัดกลุ่มเป็นหน้าลาก-วาง การรีเฟรชกลางคันจะทำให้ผู้ใช้เสียงานที่กำลังทำอยู่
    // — เหตุผลเดียวกับที่ ScheduleAdmin ถูกกันออกจากเฟสนี้ทั้งหน้า
    // จอนี้เดิม **ไม่รีเฟรชเองเลย** โค้ชจึงเห็นกลุ่มเก่าจนกว่าจะกดโหลดใหม่เอง
    if (this.coachMode) {
      this.offLive = live.subscribe(['student', 'schedule', 'booking'], () => this.fetchData(true))
    }
    // จอนี้ลากวาง ไม่มี "ใบ" ให้ล็อก — มีแต่ "ใครเปิดอยู่" ซึ่งคือสิ่งที่ §3 ข้อ 2 ต้องการพอดี
    // (โค้ช+แอดมินเปิดพร้อมกันแล้วลากเด็กคนเดียวกันคนละทาง)
    if (!this.coachMode) {
      this.presenceToken = presence.enter('groupingmanager')
      this.offPresence = presence.subscribe((p) => { this.viewers = p.viewers })
    }
  },
  beforeUnmount() {
    document.removeEventListener('mousedown', this.onDocumentMousedown, true)
    document.removeEventListener('keydown', this.onDocumentKeydown)
    if (this.offLive) this.offLive()
    if (this.offPresence) { presence.leave(this.presenceToken); this.offPresence() }
  },
  methods: {
    authHeaders() { return { headers: { Authorization: `Bearer ${this.token}` } } },
    // ปิดเมนูจัดกลุ่มเมื่อคลิกที่อื่น — แต่ "ไม่ปิด" ถ้าคลิกแถวเด็กอีกคน (.gsb-row.clickable) หรือ
    // ในตัวเมนูเอง (.popmenu): แถวเด็กจะ re-anchor เมนูไปเด็กใหม่แทนที่จะกระพริบปิด
    onDocumentMousedown(event) {
      const t = event.target
      const insideMenu = t && t.closest && (t.closest('.gsb-row.clickable') || t.closest('.gs-pv-clickable') || t.closest('.popmenu'))
      if (this.assignMenuOpen && !insideMenu) this.assignMenuOpen = false
      if (this.privateCheckinMenuOpen && !insideMenu) this.privateCheckinMenuOpen = false
    },
    onDocumentKeydown(event) {
      if (event.key !== 'Escape') return
      if (this.assignMenuOpen) this.assignMenuOpen = false
      if (this.privateCheckinMenuOpen) this.privateCheckinMenuOpen = false
    },
    handleApiError(error) {
      // ใช้ message จาก backend (ภาษาไทย) ก่อน แล้วค่อย fallback เป็นข้อความ axios
      const serverMsg = error.response && error.response.data && error.response.data.message
      if (error.response && error.response.status === 401) {
        this.$emit('onErrorHandler', serverMsg || this.$t('auth.pleaseLoginAgain'))
        this.$emit('onClickChangeState', 'login')
      } else {
        this.$emit('onErrorHandler', serverMsg || error.message)
      }
    },
    subgroupTitle(g) {
      if (g.subgroupname) return g.subgroupname
      const names = (g.coachids || []).map((id) => (this.coachMap[id] && this.coachMap[id].displayname) || `#${id}`)
      return names.join('/') || '—'
    },
    absenceChipsFor(day) {
      // 1 ป้ายต่อโค้ชต่อวัน — แสดงประเภทการลา (ถ้ามี) + เต็ม/ครึ่งวัน
      const byCoach = new Map()
      for (const a of day.absences) {
        if (byCoach.has(a.coachid)) continue
        byCoach.set(a.coachid, {
          coachid: a.coachid, displayname: a.displayname,
          dayportion: a.dayportion || 'full', leavetype: a.leavetype || null, reason: a.reason,
        })
      }
      return [...byCoach.values()].map((e) => ({
        ...e,
        label: (e.leavetype ? this.$t('grouping.leaveType_' + e.leavetype) + ' · ' : '')
          + this.$t('grouping.dayPortion_' + e.dayportion),
      }))
    },
    dayHeadLabel(dateStr) {
      return formatDateIntl(dateStr, { weekday: 'short', day: 'numeric', month: 'short' })
    },

    // ── data fetch ─────────────────────────────────────────────
    async fetchData(silent) {
      // sequence guard: กดเปลี่ยนวัน/โหมดเร็ว ๆ แล้ว response เก่ากลับมาทีหลัง ต้องไม่ทับข้อมูลล่าสุด
      const seq = (this.fetchSeq = (this.fetchSeq || 0) + 1)
      if (!silent) this.loading = true
      try {
        let res
        if (this.coachMode) {
          const startdate = this.mode === 'day' ? this.selectedDate : this.weekStart
          const enddate = this.mode === 'day' ? this.selectedDate : moment(this.weekStart, 'YYYY-MM-DD').add(6, 'days').format('YYYY-MM-DD')
          // ดึงตารางกลุ่ม + คาบ Private ของช่วงเดียวกันขนานกัน — fetchPrivate จัดการ error ของตัวเอง
          // (ล้มเหลวก็ไม่ล้มตารางกลุ่ม) และเช็ค seq เองก่อนเขียนข้อมูล
          const [mainRes] = await Promise.all([
            axios.post(this.baseURL + '/getMyCoachSchedule', { startdate, enddate }, this.authHeaders()),
            this.fetchPrivate(startdate, enddate, seq),
          ])
          res = mainRes
        } else if (this.mode === 'day') {
          res = await axios.post(this.baseURL + '/getClassGrouping', { classdate: this.selectedDate }, this.authHeaders())
        } else {
          res = await axios.post(this.baseURL + '/getWeekGrouping', { startdate: this.weekStart }, this.authHeaders())
        }
        if (seq !== this.fetchSeq) return
        if (res.data.success) {
          if (this.coachMode) this.myCoachId = res.data.mycoachid
          this.days = res.data.days || (res.data.day ? [res.data.day] : [])
          this.coaches = res.data.coaches || []
        } else if (!silent) {
          this.$emit('onErrorHandler', res.data.message || this.$t('grouping.loadFail'))
        }
      } catch (error) {
        if (seq !== this.fetchSeq) return
        // ตอน login โค้ชจะ land หน้านี้ก่อน /getMyMenus ตอบ — ถ้าสิทธิ์ถูกปิด (403) หรือ backend
        // ยังไม่มี endpoint (404) ไม่ต้องเด้ง error dialog ให้เงียบไว้ เดี๋ยว App.vue เด้งไปเมนูแรก
        // ที่เข้าได้เองเมื่อคำตอบสิทธิ์มาถึง
        const st = error.response && error.response.status
        if (this.coachMode && (st === 403 || st === 404)) return
        // 🔑 `silent` = ดึงเองเบื้องหลัง (สัญญาณเรียลไทม์) — ต้องกลืน error ตามกติกาโพลเงียบ
        // ไม่งั้น "คนอื่นเขียนข้อมูล + backend สะดุดครั้งเดียว" = dialog แดงเด้งบนจอโค้ช
        // ที่ไม่ได้แตะอะไรเลย · 401 ยังต้องผ่านเพื่อให้ SessionGuard ทำงาน
        if (silent && st !== 401) return
        this.handleApiError(error)
      } finally {
        if (seq === this.fetchSeq) this.loading = false
      }
    },
    // ── ตารางสอน Private (โหมดโค้ช) ───────────────────────────
    // เรียกคู่กับ /getMyCoachSchedule ใน fetchData — ส่ง bookingdate ด้วยเผื่อ backend เก่าที่ยังไม่รับช่วงวัน
    async fetchPrivate(startdate, enddate, seq) {
      try {
        const res = await axios.post(this.baseURL + '/getPrivateBookingList',
          { bookingdate: startdate, startdate, enddate }, this.authHeaders())
        if (seq !== this.fetchSeq) return
        if (res.data.success) {
          this.privateBookings = res.data.bookinglist || []
          this.privateSupported = true
        } else {
          this.privateBookings = []
        }
      } catch (error) {
        if (seq !== this.fetchSeq) return
        // 403/404 = backend ยังไม่เปิดสิทธิ์ coachschedule ให้ endpoint นี้ / สิทธิ์ถูกปิด — ซ่อนการ์ดเงียบ ๆ
        const st = error.response && error.response.status
        if (st === 403 || st === 404) this.privateSupported = false
        this.privateBookings = []
      }
    },
    // คาบ Private ของวันนั้น — ฟิลเตอร์ "เฉพาะของฉัน" กรองเหลือคาบที่ตัวเองสอน (เหมือนกริดกลุ่ม)
    privateForDate(dateStr) {
      let list = this.privateBookings.filter((b) => b.bookingdate === dateStr)
      if (this.onlyMine && this.myUsername) list = list.filter((b) => this.isMinePrivate(b))
      return list
    },
    // คาบ Private นี้เป็นของผู้ล็อกอินไหม (เทียบ coachusername) — ใช้ทั้งไฮไลท์และฟิลเตอร์ "ของฉัน"
    isMinePrivate(b) {
      return !!this.myUsername && b.coachusername === this.myUsername
    },
    privateTypeLabel(privatetype) {
      const map = { '1on1': 'customerCourse.private1on1', '2on1': 'customerCourse.private2on1', '3on1': 'customerCourse.private3on1' }
      return map[privatetype] ? this.$t(map[privatetype]) : privatetype
    },
    // ชื่อโค้ชผู้สอน — เผื่อชื่อว่าง fallback เป็น username (แพตเทิร์นเดียวกับ PrivateBookingList/Management)
    // สำคัญตอน head/admin เปิดหน้านี้: เห็นคาบ Private ของหลายโค้ช ต้องรู้ว่าคาบไหนของใคร
    coachDisplayName(b) {
      return (b.coachname && b.coachname.trim()) || b.coachusername || ''
    },
    privateStudentNames(b) {
      const names = (b.students || []).map((s) => s.nickname).filter(Boolean)
      let text = names.join(', ')
      if (b.othersCount) {
        const others = this.$t('privateBooking.othersCount', { n: b.othersCount })
        text = text ? `${text} + ${others}` : others
      }
      return text || '-'
    },
    // เช็คชื่อคาบ Private นี้ได้ไหม (คุม cursor + เปิดเมนู) — head/admin เช็คได้ทุกคาบ โค้ชเช็คได้
    // เฉพาะคาบที่ตัวเองสอน mirror ของ canCheckinPrivateBooking ฝั่ง backend (ที่นี่แค่ UX gate,
    // ตัวกันจริงอยู่ที่ endpoint)
    canCheckinPrivate(b) {
      return this.canCheckinAnyPrivate || (!!this.myUsername && b.coachusername === this.myUsername)
    },
    onPrivateRowClick(event, b) {
      if (!this.canCheckinPrivate(b)) return
      this.selectedPrivateBooking = b
      this.privateCheckinMenuTarget = event.currentTarget
      this.privateCheckinMenuOpen = true
    },
    onChoosePrivateCheckin() {
      this.privateCheckinMenuOpen = false
      this.$nextTick(() => { this.privateCheckinConfirmDialog = true })
    },
    async confirmPrivateCheckin() {
      const wasCheckedIn = this.privateIsCheckedIn
      this.privateCheckinConfirmDialog = false
      if (!this.selectedPrivateBooking) return
      this.$emit('onLoading', true)
      try {
        const data = await togglePrivateBookingCheckin(this.baseURL, this.token, this.selectedPrivateBooking.privatebookingid, wasCheckedIn)
        if (data.success) {
          await this.fetchData(true)
        } else {
          this.$emit('onErrorHandler',
            (data && data.message) ||
            (wasCheckedIn ? this.$t('bookingList.undoCheckinFail') : this.$t('bookingList.checkinFail')))
        }
      } catch (error) {
        this.handleApiError(error)
      } finally {
        this.$emit('onLoading', false)
      }
    },

    setMode(m) {
      if (this.mode === m) return
      this.mode = m
      this.fetchData()
    },
    goPrev() {
      const step = this.mode === 'day' ? [1, 'day'] : [7, 'days']
      this.selectedDate = moment(this.mode === 'day' ? this.selectedDate : this.weekStart, 'YYYY-MM-DD').subtract(...step).format('YYYY-MM-DD')
      this.fetchData()
    },
    goNext() {
      const step = this.mode === 'day' ? [1, 'day'] : [7, 'days']
      this.selectedDate = moment(this.mode === 'day' ? this.selectedDate : this.weekStart, 'YYYY-MM-DD').add(...step).format('YYYY-MM-DD')
      this.fetchData()
    },
    goToday() {
      this.selectedDate = moment().format('YYYY-MM-DD')
      this.fetchData()
    },
    onDatePick() { this.fetchData() },

    // ── assign ────────────────────────────────────────────────
    openAssignMenu(payload, classdate) {
      this.assignCtx = { session: payload.session, student: payload.student, currentSubgroupId: payload.currentSubgroupId, classdate }
      this.assignMenuTarget = payload.event.currentTarget
      this.assignMenuOpen = true
    },
    async doAssign(subgroupid) {
      this.assignMenuOpen = false
      if (!this.assignCtx) return
      try {
        // แถวอีเวนต์ไม่มี reservationid (คนละตารางกับใบจองคลาส) — ส่ง registrationid แทน
        const st = this.assignCtx.student
        const res = await axios.post(this.baseURL + '/assignSubgroupMember', {
          subgroupid,
          ...(st.registrationid ? { registrationid: st.registrationid } : { reservationid: st.reservationid }),
        }, this.authHeaders())
        if (!res.data.success) this.$emit('onErrorHandler', res.data.message)
        await this.fetchData(true)
      } catch (error) { this.handleApiError(error) }
    },
    async doUnassign() {
      this.assignMenuOpen = false
      if (!this.assignCtx) return
      try {
        const stu = this.assignCtx.student
        const res = await axios.post(this.baseURL + '/unassignSubgroupMember',
          stu.registrationid ? { registrationid: stu.registrationid } : { reservationid: stu.reservationid },
          this.authHeaders())
        if (!res.data.success) this.$emit('onErrorHandler', res.data.message)
        await this.fetchData(true)
      } catch (error) { this.handleApiError(error) }
    },

    // เช็คอิน/ยกเลิกเช็คอินเด็กจากหน้าตารางสอนโค้ช (โหมด coach) — toggle ตาม marker (1)
    async doCheckin(st) {
      if (!st || (!st.reservationid && !st.registrationid)) return
      const isCheckedIn = typeof st.name === 'string' ? st.name.includes('(1)') : Number(st.checkedin) === 1
      // อีเวนต์ v3 (v3 split) ไม่มีแถวใน tbooking_group — เช็คชื่อผ่าน endpoint ของอีเวนต์เอง
      const isEvent = !st.reservationid && !!st.registrationid
      const endpoint = isEvent ? '/checkinEventRegistration' : (isCheckedIn ? '/undoCheckinByAdmin' : '/checkinByAdmin')
      const body = isEvent
        ? { registrationid: st.registrationid, checkedin: isCheckedIn ? 0 : 1 }
        : { reservationid: st.reservationid, studentid: st.studentid }
      try {
        const res = await axios.post(this.baseURL + endpoint, body, this.authHeaders())
        if (!res.data.success) this.$emit('onErrorHandler', res.data.message)
        await this.fetchData(true)
      } catch (error) { this.handleApiError(error) }
    },

    // ── subgroup add / edit / delete / move-all ───────────────
    openAddGroup(session, classdate) {
      this.editingSession = session
      this.editingSubgroup = null
      this.groupForm = {
        subgroupid: null, classid: session.classid, eventid: session.eventid || null,
        classdate, classtime: session.classtime, subgroupname: '', coachids: [], remark: '',
      }
      this.moveTargetId = null
      this.groupDialog = true
    },
    openEditGroup(session, subgroup, classdate) {
      this.editingSession = session
      this.editingSubgroup = subgroup
      this.groupForm = {
        subgroupid: subgroup.subgroupid, classid: session.classid, eventid: session.eventid || null,
        classdate, classtime: session.classtime,
        subgroupname: subgroup.subgroupname || '', coachids: [...(subgroup.coachids || [])], remark: subgroup.remark || '',
      }
      this.moveTargetId = null
      this.groupDialog = true
    },
    toggleGroupCoach(coachid) {
      const i = this.groupForm.coachids.indexOf(coachid)
      if (i >= 0) this.groupForm.coachids.splice(i, 1)
      else this.groupForm.coachids.push(coachid)
    },
    async saveGroup() {
      const f = this.groupForm
      const endpoint = f.subgroupid ? '/updateSubgroup' : '/addSubgroup'
      this.$emit('onLoading', true)
      try {
        // 🐛 eventid ต้องติดไปด้วยเสมอ — คอลัมน์อีเวนต์ไม่มี classid (เป็น null) ถ้าไม่ส่ง
        // backend จะไม่รู้ว่ากลุ่มนี้ของคาบไหน แล้วตอบ "ข้อมูลไม่ครบถ้วน" (เจ้าของเจอตอนกดจริง
        // 13 ส.ค. — เทสต์ฝั่ง API ไม่จับเพราะยิง endpoint ด้วย eventid ตรง ๆ อยู่แล้ว)
        const res = await axios.post(this.baseURL + endpoint, {
          subgroupid: f.subgroupid, classid: f.classid, eventid: f.eventid,
          classdate: f.classdate, classtime: f.classtime,
          subgroupname: f.subgroupname, coachids: f.coachids, remark: f.remark,
        }, this.authHeaders())
        if (res.data.success) {
          this.groupDialog = false
          await this.fetchData(true)
        } else {
          this.$emit('onErrorHandler', res.data.message)
        }
      } catch (error) { this.handleApiError(error) }
      this.$emit('onLoading', false)
    },
    async deleteGroup() {
      this.groupDeleteDialog = false
      this.$emit('onLoading', true)
      try {
        const res = await axios.post(this.baseURL + '/deleteSubgroup', { subgroupid: this.groupForm.subgroupid }, this.authHeaders())
        if (res.data.success) {
          this.groupDialog = false
          await this.fetchData(true)
        } else {
          this.$emit('onErrorHandler', res.data.message)
        }
      } catch (error) { this.handleApiError(error) }
      this.$emit('onLoading', false)
    },
    async moveAllMembers() {
      if (!this.moveTargetId || !this.editingSubgroup) return
      this.$emit('onLoading', true)
      try {
        // กลุ่มอีเวนต์ย้ายได้เฉพาะไปกลุ่มของอีเวนต์เดียวกัน (backend ปฏิเสธการสลับราง)
        const isEv = this.editingMembers.some((m) => m.registrationid)
        const res = await axios.post(this.baseURL + '/assignSubgroupMember', {
          subgroupid: this.moveTargetId,
          ...(isEv
            ? { registrationids: this.editingMembers.map((m) => m.registrationid) }
            : { reservationids: this.editingMembers.map((m) => m.reservationid) }),
        }, this.authHeaders())
        if (res.data.success) {
          this.groupDialog = false
          await this.fetchData(true)
        } else {
          this.$emit('onErrorHandler', res.data.message)
        }
      } catch (error) { this.handleApiError(error) }
      this.$emit('onLoading', false)
    },

    // ── session note ──────────────────────────────────────────
    openNoteDialog(session, classdate) {
      this.noteForm = {
        classid: session.classid, eventid: session.eventid || null, classdate,
        label: `${session.class_label} • ${this.$formatShortDate(classdate)}`,
        remark: session.note || '',
      }
      this.noteDialog = true
    },
    async saveNote() {
      this.$emit('onLoading', true)
      try {
        // อีเวนต์เก็บหมายเหตุใน tbooking_event.note (ช่องเดียวกับหน้าจัดการอีเวนต์) — ส่ง eventid ไป
        const res = await axios.post(this.baseURL + '/saveSessionNote', {
          classid: this.noteForm.classid, eventid: this.noteForm.eventid,
          classdate: this.noteForm.classdate, remark: this.noteForm.remark,
        }, this.authHeaders())
        if (res.data.success) {
          this.noteDialog = false
          await this.fetchData(true)
        } else {
          this.$emit('onErrorHandler', res.data.message)
        }
      } catch (error) { this.handleApiError(error) }
      this.$emit('onLoading', false)
    },

    // ── day note (ต่างจาก session note — ผูกกับวันเฉย ๆ ไม่ผูกคลาส) ──
    openDayNoteDialog(classdate, label, currentNote) {
      this.dayNoteForm = { groupdate: classdate, label, remark: currentNote || '' }
      this.dayNoteDialog = true
    },
    async saveDayNote() {
      this.$emit('onLoading', true)
      try {
        const res = await axios.post(this.baseURL + '/saveDayNote', {
          groupdate: this.dayNoteForm.groupdate, remark: this.dayNoteForm.remark,
        }, this.authHeaders())
        if (res.data.success) {
          this.dayNoteDialog = false
          await this.fetchData(true)
        } else {
          this.$emit('onErrorHandler', res.data.message)
        }
      } catch (error) { this.handleApiError(error) }
      this.$emit('onLoading', false)
    },

    // ── coach absence ─────────────────────────────────────────
    // chip = null (ปุ่มบนทูลบาร์) หรือ chip ที่คลิก (แก้/ยกเลิกการลาเดิม)
    openAbsenceDialog(chip, dateStr) {
      const date = dateStr || (this.mode === 'day' ? this.selectedDate : this.weekStart)
      // originalDate = วันของการลาเดิม — ย้ายวันแล้วต้องล้างวันเดิม; ปุ่มยกเลิกชี้วันเดิมเสมอ
      const coach = chip ? this.coachMap[chip.coachid] : null
      const isFull = coach ? coach.employment_type !== 'parttime' : true
      this.absenceForm = chip
        ? { coachid: chip.coachid, absencedate: date, originalDate: date, dayportion: chip.dayportion || 'full', reason: chip.reason || '', existing: true, createLeave: isFull && !!chip.leavetype, leavetype: chip.leavetype || 'sick' }
        : { coachid: null, absencedate: date, originalDate: null, dayportion: 'full', reason: '', existing: false, createLeave: true, leavetype: 'sick' }
      this.absenceDialog = true
    },
    async saveAbsence(absent) {
      this.$emit('onLoading', true)
      try {
        const f = this.absenceForm
        // ปุ่ม "ยกเลิกการลา" ต้องล้างการลาของวันเดิม (วันของ chip) แม้ผู้ใช้จะเผลอเปลี่ยนช่องวันที่ไปแล้ว
        const targetDate = !absent && f.originalDate ? f.originalDate : f.absencedate
        // แก้การลาเดิมแล้วเปลี่ยนวัน = "ย้ายวันลา" → ล้างของวันเดิมก่อน กันกลายเป็นลาซ้ำสองวัน
        if (absent && f.existing && f.originalDate && f.originalDate !== f.absencedate) {
          await axios.post(this.baseURL + '/setCoachAbsence', {
            coachid: f.coachid, absencedate: f.originalDate, absent: false,
          }, this.authHeaders())
        }
        // สร้างใบลา (ตัดโควต้า) เฉพาะโค้ช full-time ที่ติ๊ก + ตอนบันทึก (ไม่ใช่ยกเลิก)
        const createLeave = absent && this.absenceCoachFulltime && f.createLeave
        const res = await axios.post(this.baseURL + '/setCoachAbsence', {
          coachid: f.coachid, absencedate: targetDate, absent,
          dayportion: f.dayportion, reason: f.reason,
          createLeave, leavetype: createLeave ? f.leavetype : undefined,
        }, this.authHeaders())
        if (res.data.success) {
          this.absenceDialog = false
          await this.fetchData(true)
        } else {
          this.$emit('onErrorHandler', res.data.message)
        }
      } catch (error) { this.handleApiError(error) }
      this.$emit('onLoading', false)
    },

    // ── จัดกลุ่มอัตโนมัติ ──────────────────────────────────────
    // session = null → ทุกคาบที่มีกฎของวันที่แสดงอยู่ (ปุ่มทูลบาร์); session = คาบเดียว (ปุ่มในคอลัมน์)
    openAutoDialog(session, classdate) {
      const date = classdate || this.selectedDate
      this.autoTargets = session
        ? [{ classid: session.classid, classdate: date, class_label: session.class_label }]
        : (this.dayData ? this.dayData.sessions.filter((s) => s.hasRule)
            .map((s) => ({ classid: s.classid, classdate: this.dayData.classdate, class_label: s.class_label })) : [])
      if (this.autoTargets.length === 0) return
      this.autoMode = 'unassigned_only'
      this.autoPlans = []
      this.autoDialog = true
      this.loadAutoPreview()
    },
    setAutoMode(m) {
      if (this.autoMode === m) return
      this.autoMode = m
      this.loadAutoPreview()
    },
    // ยิงพรีวิวทีละคาบขนานกัน — คาบที่ backend ตอบ success:false เก็บเป็น error รายคาบ ไม่ล้มทั้งกล่อง
    async loadAutoPreview() {
      const seq = (this.autoSeq = (this.autoSeq || 0) + 1)
      this.autoLoading = true
      try {
        const plans = await Promise.all(this.autoTargets.map(async (t) => {
          try {
            const res = await axios.post(this.baseURL + '/previewAutoGrouping', {
              classid: t.classid, classdate: t.classdate, mode: this.autoMode,
            }, this.authHeaders())
            if (!res.data.success) return { ...t, error: res.data.message, buckets: [], skipped: [] }
            return { ...t, ...res.data }
          } catch (error) {
            const msg = (error.response && error.response.data && error.response.data.message) || error.message
            return { ...t, error: msg, buckets: [], skipped: [] }
          }
        }))
        if (seq !== this.autoSeq) return
        this.autoPlans = plans
      } finally {
        if (seq === this.autoSeq) this.autoLoading = false
      }
    },
    // โหมดล้างแล้วจัดใหม่ต้องยืนยันอีกชั้น เพราะมันทับกลุ่มที่จัดมือไว้แล้ว
    confirmAuto() {
      if (this.autoMode === 'rebuild') { this.autoRebuildConfirm = true; return }
      this.applyAuto()
    },
    async applyAuto() {
      this.autoRebuildConfirm = false
      const usable = this.autoPlans.filter((p) => !p.error && p.buckets && p.buckets.length > 0)
      if (usable.length === 0) return
      this.$emit('onLoading', true)
      let moved = 0
      const failed = []
      try {
        for (const p of usable) {
          try {
            const res = await axios.post(this.baseURL + '/applyAutoGrouping', {
              classid: p.classid, classdate: p.classdate, mode: this.autoMode, buckets: p.buckets,
            }, this.authHeaders())
            if (res.data.success) moved += res.data.moved
            else failed.push(`${p.class_label}: ${res.data.message}`)
          } catch (error) {
            const msg = (error.response && error.response.data && error.response.data.message) || error.message
            failed.push(`${p.class_label}: ${msg}`)
          }
        }
        this.autoDialog = false
        await this.fetchData(true)
        if (failed.length) this.$emit('onErrorHandler', failed.join('\n'))
        else this.$emit('onInfoHandler', this.$t('grouping.autoDone', { n: moved }))
      } finally {
        this.$emit('onLoading', false)
      }
    },
    // ข้อความเหตุผล "ไม่มีข้อมูล" ต่างกันตามเกณฑ์ที่ใช้ (ไม่มีวันเกิด vs ไม่ได้กรอกเลเวล)
    skipReasonNoData(basis) {
      return basis === 'level' ? this.$t('grouping.autoSkipNoLevel') : this.$t('grouping.autoSkipNoBirthdate')
    },

    // ── ตั้งค่ากฎจัดกลุ่ม ──────────────────────────────────────
    // โหลดกติกาให้ครบก่อนเปิด (ruleLoading ยังอยู่สำหรับการรีเฟรชตอน dialog เปิดค้าง)
    async openRuleDialog() {
      this.$emit('onLoading', true)
      try {
        await this.fetchRules()
      } finally {
        this.$emit('onLoading', false)
      }
      this.ruleDialog = true
    },
    async fetchRules() {
      this.ruleLoading = true
      try {
        const res = await axios.post(this.baseURL + '/getGroupingRules', {}, this.authHeaders())
        if (res.data.success) {
          this.rules = res.data.rules || []
          this.ruleCourses = res.data.courses || []
        } else {
          this.$emit('onErrorHandler', res.data.message)
        }
      } catch (error) { this.handleApiError(error) } finally { this.ruleLoading = false }
    },
    rangeLabel(b) {
      const min = b.minval != null ? b.minval : ''
      const max = b.maxval != null ? b.maxval : ''
      if (min !== '' && max !== '') return `${min}–${max}`
      if (min !== '') return `${min}+`
      return `≤${max}`
    },
    openRuleEdit(r) {
      this.ruleForm = r
        ? {
            ruleid: r.ruleid, courseid: r.courseid, rulename: r.rulename, basis: r.basis,
            enableflag: r.enableflag, sortorder: r.sortorder,
            // แปลง null → '' ให้ช่อง number ว่างจริง (v-model กับ null จะโชว์ "null" ในบางเบราว์เซอร์)
            buckets: r.buckets.map((b) => ({
              bucketname: b.bucketname,
              minval: b.minval != null ? b.minval : '',
              maxval: b.maxval != null ? b.maxval : '',
              maxperson: b.maxperson != null ? b.maxperson : '',
              coachids: [...(b.coachids || [])],
            })),
          }
        : {
            ruleid: null, courseid: this.ruleCoursesAvailable.length ? this.ruleCoursesAvailable[0].courseid : '',
            rulename: '', basis: 'age', enableflag: 1, sortorder: this.rules.length + 1,
            buckets: [{ bucketname: '', minval: '', maxval: '', maxperson: '', coachids: [] }],
          }
      this.ruleEditDialog = true
    },
    addBucket() {
      this.ruleForm.buckets.push({ bucketname: '', minval: '', maxval: '', maxperson: '', coachids: [] })
    },
    toggleBucketCoach(bucket, coachid) {
      const i = bucket.coachids.indexOf(coachid)
      if (i >= 0) bucket.coachids.splice(i, 1)
      else bucket.coachids.push(coachid)
    },
    async saveRule() {
      this.$emit('onLoading', true)
      try {
        const res = await axios.post(this.baseURL + '/saveGroupingRule', this.ruleForm, this.authHeaders())
        if (res.data.success) {
          this.ruleEditDialog = false
          await this.fetchRules()
          // กฎเปลี่ยน → hasRule ของแต่ละคาบอาจเปลี่ยนตาม ต้องรีเฟรชกริดด้วย
          await this.fetchData(true)
        } else {
          this.$emit('onErrorHandler', res.data.message)
        }
      } catch (error) { this.handleApiError(error) }
      this.$emit('onLoading', false)
    },
    askDeleteRule(r) {
      this.ruleToDelete = r
      this.ruleDeleteDialog = true
    },
    async deleteRule() {
      this.ruleDeleteDialog = false
      this.$emit('onLoading', true)
      try {
        const res = await axios.post(this.baseURL + '/deleteGroupingRule', { ruleid: this.ruleToDelete.ruleid }, this.authHeaders())
        if (res.data.success) {
          await this.fetchRules()
          await this.fetchData(true)
        } else {
          this.$emit('onErrorHandler', res.data.message)
        }
      } catch (error) { this.handleApiError(error) }
      this.$emit('onLoading', false)
    },

    // ── coach roster ──────────────────────────────────────────
    // รายชื่อโค้ช + ตัวเลือกบัญชี staff ต้องพร้อมทั้งคู่ก่อนเปิด — ช่อง "ผูกบัญชี" ที่เติมทีหลัง
    // ทำให้เห็นเป็นช่องว่างแล้วค่อยมีตัวเลือกโผล่
    async openCoachDialog() {
      this.$emit('onLoading', true)
      try {
        await Promise.all([this.refreshCoachList(), this.fetchStaffOptions()])
      } finally {
        this.$emit('onLoading', false)
      }
      this.coachDialog = true
    },
    async refreshCoachList() {
      try {
        const res = await axios.post(this.baseURL + '/getCoachList', {}, this.authHeaders())
        if (res.data.success) this.coachList = res.data.coaches
      } catch (error) { this.handleApiError(error) }
    },
    // บัญชี staff สำหรับผูกกับโค้ช — /coachLookup (สิทธิ์คุมด้วย requireMenu ของหน้านี้; ล้มเหลวก็ยังใช้หน้าที่เหลือได้)
    async fetchStaffOptions() {
      try {
        const res = await axios.get(this.baseURL + '/coachLookup', this.authHeaders())
        if (res.data.success) {
          this.staffOptions = [
            { value: '', label: this.$t('grouping.coachLinkNone') },
            ...res.data.results.map((u) => ({ value: u.username, label: `${u.username} (${u.firstname || ''} ${u.lastname || ''})`.trim() })),
          ]
        }
      } catch (e) { this.staffOptions = [{ value: '', label: this.$t('grouping.coachLinkNone') }] }
    },
    openCoachEdit(c) {
      this.coachForm = c
        ? { coachid: c.coachid, displayname: c.displayname, color: c.color || '', sortorder: c.sortorder || 0, coachusername: c.coachusername || '', employment_type: c.employment_type === 'parttime' ? 'parttime' : 'fulltime' }
        : { coachid: null, displayname: '', color: this.palette[this.coachList.length % this.palette.length], sortorder: this.coachList.length + 1, coachusername: '', employment_type: 'fulltime' }
      this.coachEditDialog = true
    },
    async saveCoach() {
      this.$emit('onLoading', true)
      try {
        const res = await axios.post(this.baseURL + '/saveCoach', this.coachForm, this.authHeaders())
        if (res.data.success) {
          this.coachEditDialog = false
          await this.refreshCoachList()
          await this.fetchData(true)
        } else {
          this.$emit('onErrorHandler', res.data.message)
        }
      } catch (error) { this.handleApiError(error) }
      this.$emit('onLoading', false)
    },
    askDisableCoach(c) {
      this.coachToDisable = c
      this.coachDisableDialog = true
    },
    async disableCoach() {
      this.coachDisableDialog = false
      this.$emit('onLoading', true)
      try {
        const res = await axios.post(this.baseURL + '/disableCoach', { coachid: this.coachToDisable.coachid }, this.authHeaders())
        if (res.data.success) {
          await this.refreshCoachList()
          await this.fetchData(true)
        } else {
          this.$emit('onErrorHandler', res.data.message)
        }
      } catch (error) { this.handleApiError(error) }
      this.$emit('onLoading', false)
    },
  },
}
</script>

<style scoped>

.gs-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; margin-bottom: 12px; }
.gs-nav { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.gs-navbtn { padding: 6px 10px; }
.gs-navlabel { font-weight: 700; font-size: 14.5px; color: var(--c-text-heading); min-width: 150px; text-align: center; }
.gs-datejump { width: 150px; }
.gs-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.gs-modes { display: flex; gap: 6px; }
.gs-abs-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
.gs-abs-title { display: inline-flex; align-items: center; gap: 5px; font-size: 12.5px; font-weight: 700; color: var(--c-text-heading); }
.gs-abs-title .mdi { font-size: 15px; color: var(--c-error); }
.gs-abs-chip { display: inline-flex; align-items: center; gap: 5px; background: var(--c-error-bg); color: var(--c-error-tx);
  border-radius: 999px; padding: 3px 11px; font-size: 12px; font-weight: 600; }
.gs-abs-chip.sm { padding: 1px 8px; font-size: 11px; }
.gs-abs-chip.clickable { cursor: pointer; }
.gs-abs-chip .mdi { font-size: 13px; }
/* หมายเหตุประจำวัน — เหมือน .gsc-note (SheetSessionColumn) แต่เต็มความกว้าง/ระดับวัน ไม่ใช่ระดับคลาส */
.gs-daynote { display: flex; align-items: flex-start; gap: 7px; font-size: 12.5px; padding: 7px 11px;
  border: 1px dashed var(--c-border); border-radius: 10px; background: var(--c-surface-2); margin-bottom: 12px; }
.gs-daynote.sm { font-size: 11px; padding: 5px 8px; margin: 4px 0 6px; }
.gs-daynote.clickable { cursor: pointer; }
.gs-daynote.clickable:hover { background: var(--c-surface-3); }
.gs-daynote.empty { color: var(--c-text-muted); font-style: italic; }
.gs-daynote .mdi { font-size: 15px; color: var(--c-primary); margin-top: 1px; }
.gs-daynote.sm .mdi { font-size: 13px; }
.gs-daynote-text { flex: 1; min-width: 0; overflow-wrap: anywhere; white-space: pre-wrap; }
.gs-daynote-edit { opacity: 0.45; }
/* กริดอยู่ในการ์ด .scard (padding 14px เท่า Dashboard) — scrollbar แนวนอนถูกกรอบ/เว้นขอบเหมือนหน้าอื่น */
.gs-grid-card { padding: 14px; }
/* ไม่ใส่ max-width: ให้คอลัมน์ยืดเต็มเหมือน .tt-col ของ Dashboard (min เป็นพื้น แล้ว scroll เมื่อเกิน)
   กัน "เดี๋ยวเลื่อนเดี๋ยวเหลือช่องว่าง" จากการ cap ความกว้างเดิม */
.gs-col { min-width: 270px; flex: 1 0 270px; display: flex; flex-direction: column; }
.gs-daycol { min-width: 280px; flex: 1 0 280px; display: flex; flex-direction: column; gap: 10px;
  border: 1px dashed var(--c-border); border-radius: 12px; padding: 8px; }
.gs-daycol.today { border-color: var(--c-primary); border-style: solid; }
.gs-dayhead { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; font-size: 13px; padding: 2px 4px; color: var(--c-text-heading); }
.gs-dayempty { font-size: 12px; color: var(--c-text-muted); text-align: center; padding: 12px 0; }
.gs-daysession :deep(.tt-colhead) { border-radius: 10px 10px 0 0; }
.gs-coachpick { display: flex; flex-wrap: wrap; gap: 8px; }
.gs-coachopt { display: inline-flex; align-items: center; gap: 7px; border: 1px solid var(--c-border); border-radius: 999px;
  padding: 5px 12px 5px 7px; font-size: 13px; cursor: pointer; user-select: none; }
.gs-coachopt.on { border-color: var(--c-primary); background: var(--c-surface-3); }
.gs-coachopt.gs-opt-disabled { opacity: .45; cursor: not-allowed; }
.gs-coachdot { width: 12px; height: 12px; border-radius: 50%; flex: 0 0 12px; }
.gs-coachdot.lg { width: 16px; height: 16px; flex: 0 0 16px; }
.gs-moveall { display: flex; gap: 8px; align-items: center; }
.gs-moveall > :first-child { flex: 1; }
.gs-coachlist { display: flex; flex-direction: column; }
.gs-coachrow { display: flex; align-items: center; gap: 10px; padding: 9px 4px; border-bottom: 1px solid var(--c-border); }
.gs-coachrow:last-child { border-bottom: 0; }
.gs-coachrow.off { opacity: 0.55; }
.gs-coachact { font-size: 18px; cursor: pointer; color: var(--c-text-muted); padding: 2px 5px; border-radius: 7px; }
.gs-coachact:hover { background: var(--c-surface-3); }
.gs-palette { display: flex; gap: 8px; flex-wrap: wrap; }
.gs-swatch { width: 26px; height: 26px; border-radius: 8px; cursor: pointer; border: 2px solid transparent; }
.gs-swatch.on { border-color: var(--c-text-heading); box-shadow: var(--shadow-sm); }

/* ── จัดกลุ่มอัตโนมัติ: ปุ่ม + กล่องพรีวิว ── */
.gs-autobtn { border-color: var(--c-primary); color: var(--c-primary); }
.gs-auto-modes { display: flex; gap: 8px; margin-bottom: 6px; }
.gs-auto-modehint { font-size: 12px; color: var(--c-text-muted); margin-bottom: 12px; }
.gs-auto-list { display: flex; flex-direction: column; gap: 12px; }
.gs-auto-sess { display: flex; flex-direction: column; gap: 8px; }
.gs-auto-sesshead { display: flex; align-items: center; gap: 7px; flex-wrap: wrap; font-weight: 700; font-size: 13px; color: var(--c-text-heading); }
.gs-auto-sesshead .mdi { color: var(--c-primary); font-size: 15px; }
.gs-auto-bucket { border: 1px solid var(--c-border); border-radius: 10px; padding: 9px 12px; background: var(--c-surface); }
.gs-auto-bhead { display: flex; align-items: center; gap: 7px; flex-wrap: wrap; font-size: 13.5px; margin-bottom: 5px; }
.gs-auto-count { font-size: 12px; color: var(--c-text-muted); }
.gs-auto-coach { display: inline-flex; align-items: center; gap: 4px; font-size: 11.5px; color: var(--c-text-body); }
.gs-auto-names { font-size: 12.5px; color: var(--c-text-body); line-height: 1.65; overflow-wrap: anywhere; }
.gs-auto-none { font-size: 12.5px; color: var(--c-text-muted); text-align: center; padding: 14px 6px; }
.gs-auto-err { display: flex; align-items: flex-start; gap: 6px; font-size: 12.5px; padding: 8px 11px; border-radius: 10px;
  background: var(--c-surface-2); color: var(--c-text-muted); }
/* กล่องเตือน "จัดให้ไม่ได้" — โทน warning เดียวกับ badge-warning ทั้งแอป */
.gs-auto-skip { border: 1px solid var(--c-warning); border-radius: 10px; padding: 9px 12px; background: var(--c-warning-bg); }
.gs-auto-skiphead { display: flex; align-items: center; gap: 6px; font-weight: 700; font-size: 13px; color: var(--c-warning-tx); margin-bottom: 3px; }
.gs-auto-skiprow { font-size: 12.5px; color: var(--c-warning-tx); line-height: 1.6; }
.gs-auto-total { margin-right: auto; font-size: 12.5px; color: var(--c-text-muted); }

/* ── ตั้งค่ากฎ ── */
.gs-rulelist { display: flex; flex-direction: column; }
.gs-rulerow { display: flex; align-items: center; gap: 10px; padding: 10px 4px; border-bottom: 1px solid var(--c-border); }
.gs-rulerow:last-child { border-bottom: 0; }
.gs-rulerow.off { opacity: 0.55; }
.gs-bucketlist { display: flex; flex-direction: column; gap: 10px; }
.gs-bucket { border: 1px solid var(--c-border); border-radius: 10px; padding: 10px 12px; display: flex; flex-direction: column; gap: 8px; }
.gs-bucket-r1 { display: flex; align-items: center; gap: 8px; }
.gs-bucket-r1 .id-input { flex: 1; }
.gs-bucket-r2 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
.gs-bucket-r3 { display: flex; flex-direction: column; gap: 5px; }
.gs-bucket-lbl { font-size: 12px; color: var(--c-text-muted); }
.gs-coachopt.sm { padding: 3px 10px 3px 6px; font-size: 12px; }
.gs-rulehint { display: flex; align-items: flex-start; gap: 6px; font-size: 12px; color: var(--c-text-muted); margin-top: 12px; }
.gs-rulehint .mdi { font-size: 14px; }
@media (max-width: 560px) { .gs-bucket-r2 { grid-template-columns: 1fr; } }

/* ── ตารางสอน Private (โหมดโค้ช) — การ์ดต่อจากกริดคลาสกลุ่ม ── */
.gs-pv-card { margin-top: 14px; }
.gs-pv-title { display: flex; align-items: center; gap: 7px; font-weight: 700; font-size: 14px; color: var(--c-text-heading); margin-bottom: 10px; }
.gs-pv-title .mdi { color: var(--c-primary); font-size: 18px; }
/* บอกกฎเช็คชื่อให้เห็นตลอด (ไม่ใช่แค่ตอนกดพลาด) — head/admin เช็คได้ทุกคาบ โค้ชเช็คได้เฉพาะคาบตัวเอง */
.gs-pv-hint { display: flex; align-items: flex-start; gap: 5px; font-size: 12px; color: var(--c-text-muted); margin: -4px 0 10px; }
.gs-pv-hint .mdi { font-size: 14px; margin-top: 1px; }
.gs-pv-list { display: flex; flex-direction: column; gap: 8px; max-width: 560px; }
.gs-pv-item { border: 1px solid var(--c-border); border-radius: 10px; padding: 8px 12px; background: var(--c-surface); }
/* ไฮไลท์ "ของฉัน" — กรอบ + เงาเดียวกับบล็อกกลุ่มของฉัน (.gsb.mine) ให้ภาษาเดียวกันทั้งหน้า */
.gs-pv-item.gs-pv-mine { border: 2px solid var(--c-primary); box-shadow: var(--shadow-sm); }
/* เช็คชื่อได้ (canCheckinPrivate) — เฉพาะแถวนี้ที่ตอบสนองการคลิก แถวที่กดไม่ได้ยังคง cursor ปกติ */
.gs-pv-item.gs-pv-clickable { cursor: pointer; }
.gs-pv-item.gs-pv-clickable:hover { border-color: var(--c-primary); box-shadow: var(--shadow-sm); }
.gs-pv-row1 { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.gs-pv-time { display: inline-flex; align-items: center; gap: 4px; font-weight: 700; font-size: 13px; color: var(--c-text-heading); }
.gs-pv-time .mdi { color: var(--c-primary); }
.gs-pv-coach { display: flex; align-items: center; gap: 5px; font-size: 12.5px; font-weight: 600; color: var(--c-text-heading); margin-top: 5px; }
.gs-pv-coach .mdi { color: var(--c-primary); }
.gs-pv-star { color: var(--c-primary); font-size: 14px; margin-left: 2px; }
/* ยอดเงินที่ต้องเก็บ (โหมดโค้ชเท่านั้น — ลูกค้าไม่เห็น backend ไม่ส่ง price) + ป้ายจ่ายแล้ว/ยังไม่จ่าย */
.gs-pv-money { display: flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 700; color: var(--c-text-heading); margin-top: 5px; }
.gs-pv-money .mdi { color: var(--c-success); font-size: 15px; }
.gs-pv-students { display: flex; align-items: center; gap: 5px; font-size: 12.5px; margin-top: 4px; }
.gs-pv-students .mdi { color: var(--c-text-muted); }
.gs-pv-note { font-size: 12px; color: var(--c-text-muted); margin-top: 2px; }
</style>
